import { useState, useContext, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Paper,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import useHttp from '../../hooks/useHttp';
import dayjs from 'dayjs';

const STEPS = [
  { key: 'basic', label: 'Grunddaten & Entertainment' },
  { key: 'pricing', label: 'Preisfestlegung' },
  { key: 'availability', label: 'Verfügbarkeit & Öffnungszeiten' },
  { key: 'widget', label: 'Booking Widget' },
];

const REQUIRED_RIGHTS = [
  'product:create',
  'entertainment:read',
  'availability:read',
  'openingHour:read',
  'bookingWidget:read',
];

const WEEKDAYS = [
  { value: 0, label: 'Sonntag', short: 'So' },
  { value: 1, label: 'Montag', short: 'Mo' },
  { value: 2, label: 'Dienstag', short: 'Di' },
  { value: 3, label: 'Mittwoch', short: 'Mi' },
  { value: 4, label: 'Donnerstag', short: 'Do' },
  { value: 5, label: 'Freitag', short: 'Fr' },
  { value: 6, label: 'Samstag', short: 'Sa' },
];

function createEmptyNewBookingWidgetRow() {
  return {
    localId: `bw-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
    name: '',
    description: '',
    allowedPricingIndices: [],
    linkedEntertainmentIds: [],
  };
}

function buildWidgetEntertainmentPool({ selectedEntertainments, selectedAvailabilities, availabilities, productId }) {
  const pool = new Set();
  normalizeBookingWidgetEntertainmentIds(selectedEntertainments).forEach((id) => pool.add(String(id)));
  if (!productId || !Array.isArray(selectedAvailabilities) || !Array.isArray(availabilities)) {
    return Array.from(pool);
  }
  selectedAvailabilities.forEach((avId) => {
    const av = availabilities.find((a) => String(a._id) === String(avId));
    if (!av) return;
    if (Array.isArray(av.assignedEntertainments)) {
      av.assignedEntertainments.forEach((e) => {
        if (e.entertainmentId) pool.add(String(e.entertainmentId));
      });
    }
    if (Array.isArray(av.entertainmentProductRelations)) {
      av.entertainmentProductRelations.forEach((rel) => {
        if (!rel || String(rel.productId) !== String(productId)) return;
        (Array.isArray(rel.entertainmentIds) ? rel.entertainmentIds : []).forEach((eid) => {
          if (eid) pool.add(String(eid));
        });
      });
    }
  });
  return Array.from(pool);
}

function filterEntertainmentIdsToPool(ids, poolIds) {
  const pool = new Set((poolIds || []).map((id) => String(id)));
  return normalizeBookingWidgetEntertainmentIds(ids).filter((id) => pool.has(String(id)));
}

function normalizeBookingWidgetEntertainmentIds(ids) {
  if (!Array.isArray(ids)) return [];
  return ids
    .map((id) => {
      if (id == null) return null;
      const raw = id && typeof id === 'object' && id._id ? id._id : id;
      return raw;
    })
    .filter((id) => id != null && String(id).trim() !== '');
}

function buildAssignedEntertainmentsForApi(entertainmentIdList, entertainmentsList) {
  return normalizeBookingWidgetEntertainmentIds(entertainmentIdList).map((id) => {
    const ent = (entertainmentsList || []).find((e) => String(e._id) === String(id));
    return {
      entertainmentId: id,
      name: ent?.longName || ent?.entertainmentName || String(id),
    };
  });
}

function inferAssignedEntertainmentIdsForAvailabilityDraft(av, productId, fallbackEntertainmentIds) {
  const fromDoc = new Set();
  if (av && Array.isArray(av.assignedEntertainments)) {
    av.assignedEntertainments.forEach((e) => {
      const raw = e?.entertainmentId?._id || e?.entertainmentId;
      if (raw) fromDoc.add(String(raw));
    });
  }
  if (productId && av && Array.isArray(av.entertainmentProductRelations)) {
    av.entertainmentProductRelations.forEach((rel) => {
      if (!rel || String(rel.productId) !== String(productId)) return;
      (Array.isArray(rel.entertainmentIds) ? rel.entertainmentIds : []).forEach((eid) => {
        const raw = eid?._id || eid;
        if (raw) fromDoc.add(String(raw));
      });
    });
  }
  if (fromDoc.size > 0) {
    return Array.from(fromDoc);
  }
  return normalizeBookingWidgetEntertainmentIds(fallbackEntertainmentIds);
}

export default function ProductCreationWizard({ open, onClose, onSuccess, existingProduct = null }) {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();

  const [activeStep, setActiveStep] = useState(0);
  const [missingRights, setMissingRights] = useState([]);
  const [isInitializing, setIsInitializing] = useState(true);

  // Data from API
  const [entertainments, setEntertainments] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [openingHours, setOpeningHours] = useState([]);
  const [branches, setBranches] = useState([]);
  const [bookingWidgets, setBookingWidgets] = useState([]);
  const [resources, setResources] = useState([]);

  // Step 1: Basic data
  const [productData, setProductData] = useState({
    internalName: '',
    name: '',
    description: '',
    tags: '',
    minParticipants: '',
    maxParticipants: '',
    validFrom: dayjs().format('YYYY-MM-DD'),
    validTo: dayjs().add(1, 'year').format('YYYY-MM-DD'),
    selfServiceMoveCancelDisabled: false,
  });
  const [selectedEntertainments, setSelectedEntertainments] = useState([]);
  const [resourceMode, setResourceMode] = useState('none'); // 'none', 'resources', 'orderedResources'
  const [selectedResources, setSelectedResources] = useState([]);
  // For ordered resources: array of { resourceId, orderPosition, resourceDurationMinutes }
  const [selectedOrderedResources, setSelectedOrderedResources] = useState([]);
  const [showEntertainmentCreate, setShowEntertainmentCreate] = useState(false);
  const [newEntertainmentData, setNewEntertainmentData] = useState({
    entertainmentName: '',
    branch: '',
  });

  // Step 2: Pricing
  const [pricingLists, setPricingLists] = useState([]);
  const [expandedPricingList, setExpandedPricingList] = useState(null);
  const pricingListInputsRef = useRef({});

  // Step 3: Availabilities & Opening Hours
  const [selectedAvailabilities, setSelectedAvailabilities] = useState([]);
  const [selectedOpeningHours, setSelectedOpeningHours] = useState([]);
  // availabilityId -> pricingListIndex
  const [availabilityPricingSelection, setAvailabilityPricingSelection] = useState({});
  const [availabilityEdits, setAvailabilityEdits] = useState({});
  const [expandedAvailabilityEdit, setExpandedAvailabilityEdit] = useState(null);
  const [availabilitySavingById, setAvailabilitySavingById] = useState({});
  const [showAvailabilityCreate, setShowAvailabilityCreate] = useState(false);
  const [showOpeningHourCreate, setShowOpeningHourCreate] = useState(false);
  const availabilityCreateRef = useRef(null);
  const [newAvailabilityData, setNewAvailabilityData] = useState({
    name: '',
    priority: 100,
    validFrom: dayjs().format('YYYY-MM-DD'),
    validTo: dayjs().add(1, 'year').format('YYYY-MM-DD'),
    specialTypes: [],
    allowOutsideOpeningHours: false,
    hours: [],
    assignedEntertainmentIds: [],
  });
  const [newOpeningHourData, setNewOpeningHourData] = useState({
    name: '',
    priority: 100,
    validFrom: dayjs().format('YYYY-MM-DD'),
    validTo: dayjs().add(1, 'year').format('YYYY-MM-DD'),
    hours: [],
  });

  // Step 4: Booking Widget
  const [selectedBookingWidget, setSelectedBookingWidget] = useState('');
  const [createNewWidget, setCreateNewWidget] = useState(false);
  const [newBookingWidgets, setNewBookingWidgets] = useState([]);
  /** bookingWidgetId -> entertainment id strings; per-widget subset of widgetEntertainmentPoolIds */
  const [widgetEntertainmentSelections, setWidgetEntertainmentSelections] = useState({});
  const [existingLinkedBookingWidgetIds, setExistingLinkedBookingWidgetIds] = useState([]);

  // Result
  const [createdProductId, setCreatedProductId] = useState(null);
  const [createdBookingWidgetIds, setCreatedBookingWidgetIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [idCopied, setIdCopied] = useState(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const isEditMode = Boolean(existingProduct);

  const productIdForWidgetPool = createdProductId || existingProduct?._id;

  const widgetEntertainmentPoolIds = useMemo(
    () =>
      buildWidgetEntertainmentPool({
        selectedEntertainments,
        selectedAvailabilities,
        availabilities,
        productId: productIdForWidgetPool,
      }),
    [selectedEntertainments, selectedAvailabilities, availabilities, productIdForWidgetPool]
  );

  // Use refs to prevent re-runs on authCtx changes
  const authCtxRef = useRef(authCtx);
  authCtxRef.current = authCtx;
  const sendRequestRef = useRef(sendRequest);
  sendRequestRef.current = sendRequest;
  const dataLoadedRef = useRef(false);

  // Check user rights (only when open changes)
  useEffect(() => {
    if (!open) {
      dataLoadedRef.current = false;
      return;
    }

    const ctx = authCtxRef.current;
    const missing = [];
    for (const right of REQUIRED_RIGHTS) {
      if (!ctx.checkRight(right)) {
        missing.push(right);
      }
    }

    if (isEditMode) {
      if (!ctx.checkRight('product:update')) {
        missing.push('product:update');
      }
    }

    setMissingRights(missing);
  }, [open, isEditMode]);

  // Load initial data (only when open changes, not on authCtx changes)
  useEffect(() => {
    if (!open || missingRights.length > 0) {
      setIsInitializing(false);
      return;
    }

    // Prevent re-loading when authCtx changes
    if (dataLoadedRef.current) {
      return;
    }

    const loadData = async () => {
      dataLoadedRef.current = true;
      setIsInitializing(true);
      const ctx = authCtxRef.current;
      const request = sendRequestRef.current;
      
      try {
        const [entertainmentsRes, availabilitiesRes, openingHoursRes, branchesRes, bookingWidgetsRes, resourcesRes] = await Promise.all([
          request({
            url: '/api/v1/entertainment',
            method: 'GET',
            headers: ctx.getAuthHeaders(),
          }),
          request({
            url: '/api/v1/availability',
            method: 'GET',
            headers: ctx.getAuthHeaders(),
          }),
          request({
            url: '/api/v1/openingHour',
            method: 'GET',
            headers: ctx.getAuthHeaders(),
          }),
          request({
            url: '/api/v1/branch',
            method: 'GET',
            headers: ctx.getAuthHeaders(),
          }),
          request({
            url: '/api/v1/bookingWidget',
            method: 'GET',
            headers: ctx.getAuthHeaders(),
          }),
          request({
            url: '/api/v1/resource',
            method: 'GET',
            headers: ctx.getAuthHeaders(),
          }),
        ]);

        setEntertainments(Array.isArray(entertainmentsRes) ? entertainmentsRes : []);
        setAvailabilities(Array.isArray(availabilitiesRes) ? availabilitiesRes : []);
        setOpeningHours(Array.isArray(openingHoursRes) ? openingHoursRes : []);
        setBranches(Array.isArray(branchesRes) ? branchesRes : []);
        setBookingWidgets(Array.isArray(bookingWidgetsRes) ? bookingWidgetsRes : []);
        setResources(Array.isArray(resourcesRes) ? resourcesRes : []);

        // If editing, load product data
        if (existingProduct) {
          await loadExistingProduct(
            existingProduct,
            Array.isArray(bookingWidgetsRes) ? bookingWidgetsRes : [],
            Array.isArray(entertainmentsRes) ? entertainmentsRes : []
          );
        }
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Fehler beim Laden der Daten');
        dataLoadedRef.current = false;
      } finally {
        setIsInitializing(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, missingRights.length, existingProduct]);

  const loadExistingProduct = async (
    product,
    loadedBookingWidgets = [],
    loadedEntertainments = []
  ) => {
    const ctx = authCtxRef.current;
    const request = sendRequestRef.current;
    
    // Basic data
    setProductData({
      internalName: product.internalName || '',
      name: product.name || '',
      description: product.description || '',
      tags: Array.isArray(product.tags) ? product.tags.join(', ') : '',
      minParticipants: product.minParticipants ?? '',
      maxParticipants: product.maxParticipants ?? '',
      validFrom: product.validFrom ? dayjs(product.validFrom).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      validTo: product.validTo ? dayjs(product.validTo).format('YYYY-MM-DD') : dayjs().add(1, 'year').format('YYYY-MM-DD'),
      selfServiceMoveCancelDisabled: !!product.selfServiceMoveCancelDisabled,
    });

    // Pricing lists
    if (Array.isArray(product.pricing)) {
      setPricingLists(product.pricing.map((p, idx) => ({
        id: p._id || `temp-${idx}`,
        list: p.list || '',
        tags: Array.isArray(p.tags) ? p.tags.join(', ') : '',
        categories: Array.isArray(p.categories) ? p.categories.map((c, cIdx) => ({
          ...c,
          id: c._id || c.id || `cat-${idx}-${cIdx}-${Date.now()}`,
        })) : [],
        durations: Array.isArray(p.durations) ? p.durations.map((d, dIdx) => ({
          ...d,
          id: d._id || d.id || `dur-${idx}-${dIdx}-${Date.now()}`,
          durationMinutes: d.durationMS
            ? (String(d.recommendedDurationUnit || 'Minuten') === 'Stunden'
              ? Math.round(d.durationMS / (60 * 60000))
              : Math.round(d.durationMS / 60000))
            : 60,
        })) : [],
        priceMatrix: Array.isArray(p.priceMatrix) ? p.priceMatrix.map((pm, pmIdx) => ({
          ...pm,
          id: pm._id || pm.id || `pm-${idx}-${pmIdx}-${Date.now()}`,
        })) : [],
        paymentOptions: Array.isArray(p.paymentOptions) ? p.paymentOptions.join(', ') : 'online, offline',
        onlinePaymentOptions: Array.isArray(p.onlinePaymentOptions) ? p.onlinePaymentOptions.join(', ') : 'card, paypal',
      })));
    }

    // Find entertainments that have this product in their availabilities
    const productId = product._id;
    if (productId) {
      try {
        // Load availabilities and opening hours that reference this product
        const [availabilitiesRes, openingHoursRes] = await Promise.all([
          request({
            url: '/api/v1/availability',
            method: 'GET',
            headers: ctx.getAuthHeaders(),
          }),
          request({
            url: '/api/v1/openingHour',
            method: 'GET',
            headers: ctx.getAuthHeaders(),
          }),
        ]);

        const pricingIndexById = new Map(
          (Array.isArray(product.pricing) ? product.pricing : [])
            .map((p, idx) => [p?._id, idx])
            .filter(([id]) => Boolean(id))
        );

        // Find availabilities that reference this product (legacy assignedProducts and/or entertainmentProductRelations)
        const productAvailabilities = (Array.isArray(availabilitiesRes) ? availabilitiesRes : []).filter(a => {
          const inAssigned =
            Array.isArray(a.assignedProducts) &&
            a.assignedProducts.some(p => String(p.productId) === String(productId));
          const inRelations =
            Array.isArray(a.entertainmentProductRelations) &&
            a.entertainmentProductRelations.some(
              rel => rel && String(rel.productId) === String(productId)
            );
          return inAssigned || inRelations;
        });

        setSelectedAvailabilities(productAvailabilities.map(a => a._id));
        setAvailabilityPricingSelection(() => {
          const next = {};
          for (const av of productAvailabilities) {
            const assigned = Array.isArray(av.assignedProducts)
              ? av.assignedProducts.find(p => p.productId === productId)
              : null;
            const idx = assigned?.pricelistId ? pricingIndexById.get(assigned.pricelistId) : undefined;
            next[av._id] = Number.isFinite(idx) ? idx : 0;
          }
          return next;
        });

        // Entertainment selection: inferred from linked booking widgets + availability assignments
        const entIds = new Set();
        const linkedWidgets = loadedBookingWidgets.filter(w => String(w.linkedProduct) === String(productId));
        linkedWidgets.forEach((w) => {
          normalizeBookingWidgetEntertainmentIds(w.linkedEntertainments).forEach((id) => {
            entIds.add(String(id));
          });
        });
        productAvailabilities.forEach((a) => {
          if (Array.isArray(a.assignedEntertainments)) {
            a.assignedEntertainments.forEach((e) => {
              if (e.entertainmentId) entIds.add(String(e.entertainmentId));
            });
          }
          if (Array.isArray(a.entertainmentProductRelations)) {
            a.entertainmentProductRelations.forEach((rel) => {
              if (!rel || String(rel.productId) !== String(productId)) return;
              (Array.isArray(rel.entertainmentIds) ? rel.entertainmentIds : []).forEach((eid) => {
                if (eid) entIds.add(String(eid));
              });
            });
          }
        });
        setSelectedEntertainments(Array.from(entIds));

        // Find opening hours for those entertainments
        const entOpeningHours = (Array.isArray(openingHoursRes) ? openingHoursRes : []).filter(
          (oh) =>
            Array.isArray(oh.assignedEntertainments) &&
            oh.assignedEntertainments.some((e) => e.entertainmentId && entIds.has(String(e.entertainmentId)))
        );
        setSelectedOpeningHours(entOpeningHours.map(oh => oh._id));

        setAvailabilities(Array.isArray(availabilitiesRes) ? availabilitiesRes : []);
        setOpeningHours(Array.isArray(openingHoursRes) ? openingHoursRes : []);
      } catch (error) {
        console.error('Error loading product relations:', error);
      }
    }

    // Load resources - only one can be active at a time
    const loadedResourceIds = Array.isArray(product.resourceIds) 
      ? product.resourceIds.map(r => r?._id || r).filter(Boolean) 
      : [];
    const loadedOrderedResourceIds = Array.isArray(product.orderedResourceIds) 
      ? product.orderedResourceIds.map(r => r?._id || r).filter(Boolean) 
      : [];
    
    if (loadedOrderedResourceIds.length > 0) {
      const durationByResourceId = (() => {
        const map = new Map();
        const entList = Array.isArray(loadedEntertainments) ? loadedEntertainments : [];
        for (const ent of entList) {
          const rows = Array.isArray(ent?.orderedResources?.resources)
            ? ent.orderedResources.resources
            : [];
          for (const row of rows) {
            const rid = row?.resourceId ? String(row.resourceId) : null;
            const dur = Number(row?.resourceDurationMinutes || 0);
            if (!rid) continue;
            if (!(Number.isFinite(dur) && dur > 0)) continue;
            if (!map.has(rid)) {
              map.set(rid, dur);
            }
          }
        }
        return map;
      })();

      setResourceMode('orderedResources');
      // Create ordered resources with positions based on array index
      setSelectedOrderedResources(loadedOrderedResourceIds.map((id, idx) => ({
        resourceId: id,
        orderPosition: (idx + 1) * 10, // Default positions: 10, 20, 30, ...
        resourceDurationMinutes: durationByResourceId.get(String(id)) || '',
      })));
      setSelectedResources([]);
    } else if (loadedResourceIds.length > 0) {
      setResourceMode('resources');
      setSelectedResources(loadedResourceIds);
      setSelectedOrderedResources([]);
    } else {
      setResourceMode('none');
      setSelectedResources([]);
      setSelectedOrderedResources([]);
    }

    setCreatedProductId(productId);

    const linkedWidgets = loadedBookingWidgets.filter(w => w.linkedProduct === productId);
    const linkedIds = linkedWidgets.map(w => w._id).filter(Boolean);
    setExistingLinkedBookingWidgetIds(linkedIds);
    const initialWidgetEnt = {};
    linkedWidgets.forEach((w) => {
      initialWidgetEnt[String(w._id)] = normalizeBookingWidgetEntertainmentIds(w.linkedEntertainments);
    });
    setWidgetEntertainmentSelections(initialWidgetEnt);
    if (linkedWidgets.length === 1) {
      setSelectedBookingWidget(linkedWidgets[0]._id);
    } else {
      setSelectedBookingWidget('');
    }
  };

  // Request close - shows confirmation dialog
  const requestClose = () => {
    // If product was already saved, close directly
    if (createdProductId) {
      handleClose();
      return;
    }
    setShowCloseConfirm(true);
  };

  // Actually close and reset
  const handleClose = () => {
    setShowCloseConfirm(false);
    // Reset state
    setActiveStep(0);
    setProductData({
      internalName: '',
      name: '',
      description: '',
      tags: '',
      minParticipants: '',
      maxParticipants: '',
      validFrom: dayjs().format('YYYY-MM-DD'),
      validTo: dayjs().add(1, 'year').format('YYYY-MM-DD'),
      selfServiceMoveCancelDisabled: false,
    });
    setSelectedEntertainments([]);
    setResourceMode('none');
    setSelectedResources([]);
    setSelectedOrderedResources([]);
    setPricingLists([]);
    setSelectedAvailabilities([]);
    setSelectedOpeningHours([]);
    setAvailabilityPricingSelection({});
    setSelectedBookingWidget('');
    setCreateNewWidget(false);
    setNewBookingWidgets([]);
    setWidgetEntertainmentSelections({});
    setExistingLinkedBookingWidgetIds([]);
    setCreatedProductId(null);
    setCreatedBookingWidgetIds([]);
    setShowEntertainmentCreate(false);
    setShowAvailabilityCreate(false);
    setShowOpeningHourCreate(false);
    setAvailabilityEdits({});
    setNewAvailabilityData({
      name: '',
      priority: 100,
      validFrom: dayjs().format('YYYY-MM-DD'),
      validTo: dayjs().add(1, 'year').format('YYYY-MM-DD'),
      specialTypes: [],
      allowOutsideOpeningHours: false,
      hours: [],
      assignedEntertainmentIds: [],
    });
    onClose();
  };

  const canProceedToNextStep = useCallback(() => {
    switch (activeStep) {
      case 0: // Basic & Entertainment
        return (
          productData.internalName.trim() !== '' &&
          productData.name.trim() !== '' &&
          productData.validFrom !== '' &&
          productData.validTo !== '' &&
          selectedEntertainments.length > 0
        );
      case 1: // Pricing
        return pricingLists.length > 0 && pricingLists.every((pl) =>
          pl.list.trim() !== '' &&
          pl.durations.length > 0 &&
          pl.categories.length > 0
        );
      case 2: // Availability & Opening Hours
        return selectedAvailabilities.length > 0 || selectedOpeningHours.length > 0;
      case 3: // Widget
        return true;
      default:
        return false;
    }
  }, [activeStep, productData, selectedEntertainments, pricingLists, selectedAvailabilities, selectedOpeningHours]);

  const handleNext = () => {
    if (activeStep < STEPS.length - 1) {
      if (activeStep === 1) {
        setPricingLists((prev) => prev.map((pl) => {
          if (!pl?.categories?.length || !pl?.durations?.length) return pl;
          if (Array.isArray(pl.priceMatrix) && pl.priceMatrix.length > 0) return pl;

          const matrix = [];
          for (const cat of pl.categories) {
            for (const dur of pl.durations) {
              const existing = pl.priceMatrix?.find(
                (pm) => pm.category === cat.name && pm.durationMS === dur.durationMS
              );
              matrix.push({
                id: existing?.id || `pm-${Date.now()}-${Math.random()}`,
                category: cat.name,
                durationMS: dur.durationMS,
                prices: existing?.prices || [{ minPersons: 1, price: 0 }],
                minPrice: existing?.minPrice || 0,
              });
            }
          }
          return { ...pl, priceMatrix: matrix };
        }));
      }
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(prev => prev - 1);
    }
  };

  const handleStepClick = (stepIndex) => {
    if (stepIndex === activeStep) return;
    if (stepIndex < activeStep) {
      setActiveStep(stepIndex);
      return;
    }
    if (!canProceedToNextStep()) {
      toast.error('Bitte zuerst die Pflichtfelder im aktuellen Schritt ausfüllen');
      return;
    }
    setActiveStep(stepIndex);
  };

  // Keep availability -> pricing mapping in sync with selection
  useEffect(() => {
    setAvailabilityPricingSelection(prev => {
      const next = {};
      for (const avId of selectedAvailabilities) {
        const existing = prev?.[avId];
        next[avId] = Number.isFinite(existing) ? existing : 0;
      }
      return next;
    });
  }, [selectedAvailabilities]);

  useEffect(() => {
    setAvailabilityEdits(prev => {
      const next = { ...(prev || {}) };

      for (const avId of selectedAvailabilities) {
        if (next[avId]) continue;
        const av = availabilities.find(a => a._id === avId);
        if (!av) continue;
        const assignedEntertainmentIds = inferAssignedEntertainmentIdsForAvailabilityDraft(
          av,
          productIdForWidgetPool,
          selectedEntertainments
        );
        // Seed the per-relation time-slot override from an existing relation for
        // this product, if any. Empty means "use the availability slots above".
        const existingRelation = Array.isArray(av.entertainmentProductRelations)
          ? av.entertainmentProductRelations.find(
              r => String(r.productId) === String(productIdForWidgetPool)
            )
          : null;
        next[avId] = {
          name: av.name || '',
          priority: av.priority ?? 100,
          validFrom: av.validFrom ? dayjs(av.validFrom).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
          validTo: av.validTo ? dayjs(av.validTo).format('YYYY-MM-DD') : dayjs().add(1, 'year').format('YYYY-MM-DD'),
          specialTypes: Array.isArray(av.specialTypes) ? av.specialTypes : [],
          allowOutsideOpeningHours: Boolean(av.allowOutsideOpeningHours),
          hours: Array.isArray(av.hours)
            ? av.hours.map(h => ({
                dayCode: Array.isArray(h.dayCode) ? h.dayCode : [],
                start: h.start || '09:00',
                end: h.end || '22:00',
              }))
            : [],
          relationHoursOverride:
            existingRelation && Array.isArray(existingRelation.hours)
              ? existingRelation.hours.map(h => ({
                  dayCode: Array.isArray(h.dayCode) ? h.dayCode : [],
                  start: h.start || '09:00',
                  end: h.end || '22:00',
                  durationsMinutes: Array.isArray(h.durationsMinutes) ? h.durationsMinutes : [],
                }))
              : [],
          assignedEntertainmentIds,
        };
      }

      for (const id of Object.keys(next)) {
        if (!selectedAvailabilities.includes(id)) {
          delete next[id];
        }
      }

      return next;
    });

    if (expandedAvailabilityEdit && !selectedAvailabilities.includes(expandedAvailabilityEdit)) {
      setExpandedAvailabilityEdit(null);
    }
  }, [
    selectedAvailabilities,
    availabilities,
    expandedAvailabilityEdit,
    selectedEntertainments,
    productIdForWidgetPool,
  ]);

  useEffect(() => {
    if (!showAvailabilityCreate) return;
    const el = availabilityCreateRef.current;
    if (!el || typeof el.scrollIntoView !== 'function') return;
    setTimeout(() => {
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch {
        el.scrollIntoView();
      }
    }, 0);
  }, [showAvailabilityCreate]);

  useEffect(() => {
    if (!showAvailabilityCreate) return;
    setNewAvailabilityData((prev) => ({
      ...prev,
      assignedEntertainmentIds:
        Array.isArray(prev.assignedEntertainmentIds) && prev.assignedEntertainmentIds.length > 0
          ? prev.assignedEntertainmentIds
          : normalizeBookingWidgetEntertainmentIds(selectedEntertainments),
    }));
  }, [showAvailabilityCreate, selectedEntertainments]);

  // Create entertainment on-the-fly
  const handleCreateEntertainment = async () => {
    if (!newEntertainmentData.entertainmentName.trim() || !newEntertainmentData.branch) {
      toast.error('Name und Filiale sind erforderlich');
      return;
    }

    try {
      const response = await sendRequest({
        url: '/api/v1/entertainment',
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: {
          entertainmentName: newEntertainmentData.entertainmentName,
          branch: newEntertainmentData.branch,
        },
      });

      if (response && response._id) {
        setEntertainments(prev => [...prev, response]);
        setSelectedEntertainments(prev => [...prev, response._id]);
        setNewEntertainmentData({ entertainmentName: '', branch: '' });
        setShowEntertainmentCreate(false);
        toast.success('Entertainment erstellt');
      }
    } catch (error) {
      console.error('Error creating entertainment:', error);
      toast.error('Fehler beim Erstellen des Entertainments');
    }
  };

  // Add new pricing list
  const addPricingList = () => {
    setPricingLists(prev => [
      ...prev,
      {
        id: `temp-${Date.now()}`,
        list: `Preisliste ${prev.length + 1}`,
        tags: '',
        categories: [{ name: 'Personen', shortName: 'Pers.', description: 'Personen' }],
        durations: [
          {
            id: `dur-${Date.now()}`,
            durationMinutes: 60,
            durationMS: 60 * 60000,
            recommendedDurationUnit: 'Minuten',
            durationDescription: '',
            isBestseller: false,
            isUserBookingRestricted: false,
          },
        ],
        priceMatrix: [],
        paymentOptions: 'online, offline',
        onlinePaymentOptions: 'card, paypal',
      },
    ]);
  };

  // Remove pricing list
  const removePricingList = (id) => {
    setPricingLists(prev => prev.filter(pl => pl.id !== id));
  };

  // Update pricing list field
  const updatePricingList = (id, field, value) => {
    setPricingLists(prev => prev.map(pl => pl.id === id ? { ...pl, [field]: value } : pl));
  };

  // Add duration to pricing list
  const addDuration = (listId) => {
    setPricingLists(prev => prev.map(pl => {
      if (pl.id !== listId) return pl;
      return {
        ...pl,
        durations: [
          ...pl.durations,
          {
            id: `dur-${Date.now()}`,
            durationMinutes: 60,
            durationMS: 60 * 60000,
            recommendedDurationUnit: 'Minuten',
            durationDescription: '',
            isBestseller: false,
            isUserBookingRestricted: false,
          },
        ],
      };
    }));
  };

  // Remove duration
  const removeDuration = (listId, durId) => {
    setPricingLists(prev => prev.map(pl => {
      if (pl.id !== listId) return pl;
      return {
        ...pl,
        durations: pl.durations.filter(d => d.id !== durId && d._id !== durId),
        priceMatrix: pl.priceMatrix.filter(pm => {
          const dur = pl.durations.find(d => d.id === durId || d._id === durId);
          return dur ? pm.durationMS !== dur.durationMS : true;
        }),
      };
    }));
  };

  // Update duration
  const updateDuration = (listId, durId, field, value) => {
    setPricingLists(prev => prev.map(pl => {
      if (pl.id !== listId) return pl;
      const durationBefore = pl.durations.find(d => d.id === durId || d._id === durId);
      const oldDurationMS = durationBefore?.durationMS;
      return {
        ...pl,
        durations: pl.durations.map(d => {
          if (d.id !== durId && d._id !== durId) return d;
          const updated = { ...d, [field]: value };
          if (field === 'durationMinutes' || field === 'recommendedDurationUnit') {
            const unit = String(updated.recommendedDurationUnit || 'Minuten');
            const factor = unit === 'Stunden' ? 60 * 60000 : 60000;
            const amount = parseFloat(updated.durationMinutes);
            const safeAmount = Number.isFinite(amount) ? amount : 0;
            updated.durationMS = safeAmount * factor;
          }
          return updated;
        }),
        priceMatrix: (field === 'durationMinutes' || field === 'recommendedDurationUnit') && Number.isFinite(oldDurationMS)
          ? pl.priceMatrix.map(pm => {
              if (pm.durationMS !== oldDurationMS) return pm;
              const unit = String(durationBefore?.recommendedDurationUnit || 'Minuten');
              const amount = field === 'durationMinutes'
                ? parseFloat(value)
                : parseFloat(durationBefore?.durationMinutes);
              const safeAmount = Number.isFinite(amount) ? amount : 0;
              const targetUnit = field === 'recommendedDurationUnit'
                ? String(value || 'Minuten')
                : unit;
              const factor = targetUnit === 'Stunden' ? 60 * 60000 : 60000;
              return { ...pm, durationMS: safeAmount * factor };
            })
          : pl.priceMatrix,
      };
    }));
  };

  // Add category to pricing list
  const addCategory = (listId) => {
    setPricingLists(prev => prev.map(pl => {
      if (pl.id !== listId) return pl;
      const newCatNum = pl.categories.length + 1;
      const baseName = 'Personen';
      const baseShort = 'Pers.';
      const nameAlreadyUsed = pl.categories.some(c => c?.name === baseName);
      const shortAlreadyUsed = pl.categories.some(c => c?.shortName === baseShort);
      const name = nameAlreadyUsed ? `${baseName} ${newCatNum}` : baseName;
      return {
        ...pl,
        categories: [
          ...pl.categories,
          {
            id: `cat-${Date.now()}`,
            name,
            shortName: shortAlreadyUsed ? `${baseShort}${newCatNum}` : baseShort,
            description: name,
          },
        ],
      };
    }));
  };

  // Remove category
  const removeCategory = (listId, catId) => {
    setPricingLists(prev => prev.map(pl => {
      if (pl.id !== listId) return pl;
      const cat = pl.categories.find(c => c.id === catId || c._id === catId);
      return {
        ...pl,
        categories: pl.categories.filter(c => c.id !== catId && c._id !== catId),
        priceMatrix: pl.priceMatrix.filter(pm => cat ? pm.category !== cat.name : true),
      };
    }));
  };

  // Update category
  const updateCategory = (listId, catId, field, value) => {
    setPricingLists(prev => prev.map(pl => {
      if (pl.id !== listId) return pl;
      return {
        ...pl,
        categories: pl.categories.map(c => 
          (c.id === catId || c._id === catId) ? { ...c, [field]: value } : c
        ),
      };
    }));
  };

  // Generate price matrix for a pricing list
  const generatePriceMatrix = (listId) => {
    setPricingLists(prev => prev.map(pl => {
      if (pl.id !== listId) return pl;
      
      const matrix = [];
      for (const cat of pl.categories) {
        for (const dur of pl.durations) {
          const existing = pl.priceMatrix.find(
            pm => pm.category === cat.name && pm.durationMS === dur.durationMS
          );
          
          matrix.push({
            id: existing?.id || `pm-${Date.now()}-${Math.random()}`,
            category: cat.name,
            durationMS: dur.durationMS,
            prices: existing?.prices || [{ minPersons: 1, price: 0 }],
            minPrice: existing?.minPrice || 0,
          });
        }
      }
      
      return { ...pl, priceMatrix: matrix };
    }));
  };

  // Update price matrix entry
  const updatePriceMatrixEntry = (listId, pmId, priceIndex, field, value) => {
    setPricingLists(prev => prev.map(pl => {
      if (pl.id !== listId) return pl;
      return {
        ...pl,
        priceMatrix: pl.priceMatrix.map(pm => {
          if (pm.id !== pmId) return pm;
          const newPrices = [...pm.prices];
          if (priceIndex !== null && priceIndex !== undefined) {
            newPrices[priceIndex] = { ...newPrices[priceIndex], [field]: parseFloat(value) || 0 };
          }
          const minPrice = Math.min(...newPrices.map(p => p.price));
          return { ...pm, prices: newPrices, minPrice };
        }),
      };
    }));
  };

  // Add tier price to matrix entry
  const addTierPrice = (listId, pmId) => {
    setPricingLists(prev => prev.map(pl => {
      if (pl.id !== listId) return pl;
      return {
        ...pl,
        priceMatrix: pl.priceMatrix.map(pm => {
          if (pm.id !== pmId) return pm;
          const lastTier = pm.prices[pm.prices.length - 1];
          return {
            ...pm,
            prices: [
              ...pm.prices,
              { minPersons: (lastTier?.minPersons || 0) + 1, price: lastTier?.price || 0 },
            ],
          };
        }),
      };
    }));
  };

  // Remove tier price
  const removeTierPrice = (listId, pmId, priceIndex) => {
    setPricingLists(prev => prev.map(pl => {
      if (pl.id !== listId) return pl;
      return {
        ...pl,
        priceMatrix: pl.priceMatrix.map(pm => {
          if (pm.id !== pmId) return pm;
          if (pm.prices.length <= 1) return pm;
          const newPrices = pm.prices.filter((_, idx) => idx !== priceIndex);
          const minPrice = Math.min(...newPrices.map(p => p.price));
          return { ...pm, prices: newPrices, minPrice };
        }),
      };
    }));
  };

  // Create availability on-the-fly
  const handleCreateAvailability = async () => {
    if (!newAvailabilityData.name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }

    try {
      const entIdsForCreate =
        Array.isArray(newAvailabilityData.assignedEntertainmentIds) &&
        newAvailabilityData.assignedEntertainmentIds.length > 0
          ? newAvailabilityData.assignedEntertainmentIds
          : selectedEntertainments;

      if (!Array.isArray(entIdsForCreate) || entIdsForCreate.length === 0) {
        toast.error('Bitte mindestens ein Entertainment zuordnen (Schritt 1 oder hier in der Auswahl).');
        return;
      }

      const response = await sendRequest({
        url: '/api/v1/availability',
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: {
          name: newAvailabilityData.name,
          priority: newAvailabilityData.priority,
          validFrom: newAvailabilityData.validFrom,
          validTo: newAvailabilityData.validTo,
          specialTypes: Array.isArray(newAvailabilityData.specialTypes) ? newAvailabilityData.specialTypes : [],
          allowOutsideOpeningHours: Boolean(newAvailabilityData.allowOutsideOpeningHours),
          hours: newAvailabilityData.hours,
          assignedEntertainments: buildAssignedEntertainmentsForApi(entIdsForCreate, entertainments),
        },
      });

      if (response && response._id) {
        setAvailabilities(prev => [...prev, response]);
        setSelectedAvailabilities(prev => [...prev, response._id]);
        setNewAvailabilityData({
          name: '',
          priority: 100,
          validFrom: dayjs().format('YYYY-MM-DD'),
          validTo: dayjs().add(1, 'year').format('YYYY-MM-DD'),
          specialTypes: [],
          allowOutsideOpeningHours: false,
          hours: [],
          assignedEntertainmentIds: [],
        });
        setShowAvailabilityCreate(false);
        toast.success('Verfügbarkeit erstellt');
      }
    } catch (error) {
      console.error('Error creating availability:', error);
      toast.error('Fehler beim Erstellen der Verfügbarkeit');
    }
  };

  // Create opening hour on-the-fly
  const handleCreateOpeningHour = async () => {
    if (!newOpeningHourData.name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }

    try {
      const response = await sendRequest({
        url: '/api/v1/openingHour',
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: {
          name: newOpeningHourData.name,
          priority: newOpeningHourData.priority,
          validFrom: newOpeningHourData.validFrom,
          validTo: newOpeningHourData.validTo,
          hours: newOpeningHourData.hours,
          assignedEntertainments: selectedEntertainments.map(id => {
            const ent = entertainments.find(e => e._id === id);
            return { entertainmentId: id, name: ent?.longName || ent?.entertainmentName || '' };
          }),
        },
      });

      if (response && response._id) {
        setOpeningHours(prev => [...prev, response]);
        setSelectedOpeningHours(prev => [...prev, response._id]);
        setNewOpeningHourData({
          name: '',
          priority: 100,
          validFrom: dayjs().format('YYYY-MM-DD'),
          validTo: dayjs().add(1, 'year').format('YYYY-MM-DD'),
          hours: [],
        });
        setShowOpeningHourCreate(false);
        toast.success('Öffnungszeit erstellt');
      }
    } catch (error) {
      console.error('Error creating opening hour:', error);
      toast.error('Fehler beim Erstellen der Öffnungszeit');
    }
  };

  const updateAvailabilityEdit = (availabilityId, patch) => {
    setAvailabilityEdits(prev => ({
      ...(prev || {}),
      [availabilityId]: {
        ...(prev?.[availabilityId] || {}),
        ...(patch || {}),
      },
    }));
  };

  const addAvailabilityEditTimeSlot = (availabilityId) => {
    setAvailabilityEdits(prev => {
      const current = prev?.[availabilityId];
      if (!current) return prev;
      return {
        ...(prev || {}),
        [availabilityId]: {
          ...current,
          hours: [
            ...(Array.isArray(current.hours) ? current.hours : []),
            { dayCode: [1, 2, 3, 4, 5], start: '09:00', end: '22:00' },
          ],
        },
      };
    });
  };

  const updateAvailabilityEditTimeSlot = (availabilityId, index, field, value) => {
    setAvailabilityEdits(prev => {
      const current = prev?.[availabilityId];
      if (!current) return prev;
      const hours = Array.isArray(current.hours) ? current.hours : [];
      return {
        ...(prev || {}),
        [availabilityId]: {
          ...current,
          hours: hours.map((h, i) => (i === index ? { ...h, [field]: value } : h)),
        },
      };
    });
  };

  const removeAvailabilityEditTimeSlot = (availabilityId, index) => {
    setAvailabilityEdits(prev => {
      const current = prev?.[availabilityId];
      if (!current) return prev;
      const hours = Array.isArray(current.hours) ? current.hours : [];
      return {
        ...(prev || {}),
        [availabilityId]: {
          ...current,
          hours: hours.filter((_, i) => i !== index),
        },
      };
    });
  };

  // Per-relation time-slot override editors. When non-empty these slots replace
  // the availability-level slots for this product on this availability.
  const addRelationOverrideTimeSlot = (availabilityId) => {
    setAvailabilityEdits(prev => {
      const current = prev?.[availabilityId];
      if (!current) return prev;
      return {
        ...(prev || {}),
        [availabilityId]: {
          ...current,
          relationHoursOverride: [
            ...(Array.isArray(current.relationHoursOverride) ? current.relationHoursOverride : []),
            { dayCode: [1, 2, 3, 4, 5], start: '09:00', end: '22:00', durationsMinutes: [] },
          ],
        },
      };
    });
  };

  const updateRelationOverrideTimeSlot = (availabilityId, index, field, value) => {
    setAvailabilityEdits(prev => {
      const current = prev?.[availabilityId];
      if (!current) return prev;
      const hours = Array.isArray(current.relationHoursOverride) ? current.relationHoursOverride : [];
      return {
        ...(prev || {}),
        [availabilityId]: {
          ...current,
          relationHoursOverride: hours.map((h, i) => (i === index ? { ...h, [field]: value } : h)),
        },
      };
    });
  };

  const removeRelationOverrideTimeSlot = (availabilityId, index) => {
    setAvailabilityEdits(prev => {
      const current = prev?.[availabilityId];
      if (!current) return prev;
      const hours = Array.isArray(current.relationHoursOverride) ? current.relationHoursOverride : [];
      return {
        ...(prev || {}),
        [availabilityId]: {
          ...current,
          relationHoursOverride: hours.filter((_, i) => i !== index),
        },
      };
    });
  };

  const handleUpdateAvailability = async (availabilityId) => {
    const draft = availabilityEdits?.[availabilityId];
    if (!draft?.name?.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }

    setAvailabilitySavingById(prev => ({ ...(prev || {}), [availabilityId]: true }));
    try {
      const body = {
        name: draft.name,
        priority: Number(draft.priority),
        validFrom: draft.validFrom,
        validTo: draft.validTo,
        specialTypes: Array.isArray(draft.specialTypes) ? draft.specialTypes : [],
        allowOutsideOpeningHours: Boolean(draft.allowOutsideOpeningHours),
        hours: (Array.isArray(draft.hours) ? draft.hours : []).map(h => ({
          dayCode: (Array.isArray(h.dayCode) ? h.dayCode : [])
            .map(n => Number(n))
            .filter(n => Number.isFinite(n)),
          start: h.start,
          end: h.end,
        })),
      };

      if (Array.isArray(draft.assignedEntertainmentIds)) {
        body.assignedEntertainments = buildAssignedEntertainmentsForApi(draft.assignedEntertainmentIds, entertainments);
      }

      const response = await sendRequest({
        url: `/api/v1/availability/${availabilityId}`,
        method: 'PATCH',
        headers: authCtx.getAuthHeaders(),
        body,
      });

      if (response && response._id) {
        const responseEntIds = inferAssignedEntertainmentIdsForAvailabilityDraft(
          response,
          productIdForWidgetPool,
          selectedEntertainments
        );
        setAvailabilities(prev => prev.map(a => (a._id === availabilityId ? response : a)));
        setAvailabilityEdits(prev => ({
          ...(prev || {}),
          [availabilityId]: {
            name: response.name || body.name,
            priority: response.priority ?? body.priority,
            validFrom: response.validFrom ? dayjs(response.validFrom).format('YYYY-MM-DD') : body.validFrom,
            validTo: response.validTo ? dayjs(response.validTo).format('YYYY-MM-DD') : body.validTo,
            specialTypes: Array.isArray(response.specialTypes) ? response.specialTypes : body.specialTypes,
            allowOutsideOpeningHours: Boolean(response.allowOutsideOpeningHours),
            hours: Array.isArray(response.hours)
              ? response.hours.map(h => ({
                  dayCode: Array.isArray(h.dayCode) ? h.dayCode : [],
                  start: h.start || '09:00',
                  end: h.end || '22:00',
                }))
              : body.hours,
            assignedEntertainmentIds: responseEntIds.length > 0 ? responseEntIds : draft.assignedEntertainmentIds,
          },
        }));
        toast.success('Verfügbarkeit aktualisiert');
      }
    } catch (error) {
      console.error('Error updating availability:', error);
      toast.error('Fehler beim Aktualisieren der Verfügbarkeit');
    } finally {
      setAvailabilitySavingById(prev => ({ ...(prev || {}), [availabilityId]: false }));
    }
  };

  // Add time slot to new availability/opening hour
  const addTimeSlot = (type) => {
    const setter = type === 'availability' ? setNewAvailabilityData : setNewOpeningHourData;
    setter(prev => ({
      ...prev,
      hours: [
        ...prev.hours,
        { dayCode: [1, 2, 3, 4, 5], start: '09:00', end: '22:00', isClosed: false },
      ],
    }));
  };

  // Update time slot
  const updateTimeSlot = (type, index, field, value) => {
    const setter = type === 'availability' ? setNewAvailabilityData : setNewOpeningHourData;
    setter(prev => ({
      ...prev,
      hours: prev.hours.map((h, i) => i === index ? { ...h, [field]: value } : h),
    }));
  };

  // Remove time slot
  const removeTimeSlot = (type, index) => {
    const setter = type === 'availability' ? setNewAvailabilityData : setNewOpeningHourData;
    setter(prev => ({
      ...prev,
      hours: prev.hours.filter((_, i) => i !== index),
    }));
  };

  // Helper to safely convert string/array to array
  const toArray = (value, defaultValue = []) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      return value.split(',').map(t => t.trim()).filter(Boolean);
    }
    return defaultValue;
  };

  // Save everything
  const handleSave = async () => {
    setIsSaving(true);

    try {
      if (resourceMode === 'orderedResources' && selectedOrderedResources.length > 0) {
        const hasInvalidDuration = selectedOrderedResources.some(r => {
          const v = Number(r?.resourceDurationMinutes || 0);
          return !(Number.isFinite(v) && v > 0);
        });
        if (hasInvalidDuration) {
          toast.error('Bitte Dauer (Min.) für alle geordneten Ressourcen setzen');
          return;
        }
      }

      const pricingInputs = pricingListInputsRef.current || {};
      // Prepare pricing data - preserve _id to avoid breaking availability references
      const pricingData = pricingLists.map(pl => {
        const listDraft = pricingInputs?.[pl.id];
        const listValue = typeof listDraft?.list === 'string' ? listDraft.list : (pl.list || '');
        const tagsValue = typeof listDraft?.tags === 'string' ? listDraft.tags : pl.tags;
        const pricingEntry = {
          list: listValue,
          tags: toArray(tagsValue),
          categories: (pl.categories || []).map(c => {
            const description = String(c.description || '').trim() || String(c.name || '').trim() || 'Kategorie';
            const cat = {
              name: c.name,
              shortName: c.shortName,
              description,
            };
            if (c._id) cat._id = c._id;
            return cat;
          }),
          durations: (pl.durations || []).map(d => {
            const dur = {
              durationMS: d.durationMS,
              recommendedDurationUnit: d.recommendedDurationUnit || 'Minuten',
              durationDescription: d.durationDescription || '',
              isBestseller: d.isBestseller || false,
              isUserBookingRestricted: d.isUserBookingRestricted || false,
            };
            if (d._id) dur._id = d._id;
            return dur;
          }),
          priceMatrix: (pl.priceMatrix || []).map(pm => {
            const entry = {
              category: pm.category,
              durationMS: pm.durationMS,
              prices: (pm.prices || []).map(p => {
                const price = { minPersons: p.minPersons, price: p.price };
                if (p._id) price._id = p._id;
                return price;
              }),
              minPrice: pm.minPrice || 0,
            };
            if (pm._id) entry._id = pm._id;
            return entry;
          }),
          paymentOptions: toArray(pl.paymentOptions, ['online', 'offline']),
          onlinePaymentOptions: toArray(pl.onlinePaymentOptions, ['card', 'paypal']),
        };
        // Preserve pricing _id to avoid breaking availability pricelistId references
        if (pl.id && !pl.id.startsWith('temp-')) {
          pricingEntry._id = pl.id;
        }
        return pricingEntry;
      });

      // Create or update product
      let productId = createdProductId;

      if (isEditMode && productId) {
        // Update existing product
        await sendRequest({
          url: `/api/v1/product/${productId}`,
          method: 'PATCH',
          headers: authCtx.getAuthHeaders(),
          body: {
            internalName: productData.internalName,
            name: productData.name,
            description: productData.description,
            tags: toArray(productData.tags),
            minParticipants: productData.minParticipants ? parseInt(productData.minParticipants, 10) : null,
            maxParticipants: productData.maxParticipants ? parseInt(productData.maxParticipants, 10) : null,
            validFrom: productData.validFrom,
            validTo: productData.validTo,
            selfServiceMoveCancelDisabled: !!productData.selfServiceMoveCancelDisabled,
            pricing: pricingData,
            resourceIds: resourceMode === 'resources' ? selectedResources : [],
            orderedResourceIds: resourceMode === 'orderedResources' 
              ? [...selectedOrderedResources].sort((a, b) => a.orderPosition - b.orderPosition).map(r => r.resourceId)
              : [],
          },
        });
      } else {
        // Create new product
        const productResponse = await sendRequest({
          url: '/api/v1/product',
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: {
            internalName: productData.internalName,
            name: productData.name,
            description: productData.description,
            tags: toArray(productData.tags),
            minParticipants: productData.minParticipants ? parseInt(productData.minParticipants, 10) : null,
            maxParticipants: productData.maxParticipants ? parseInt(productData.maxParticipants, 10) : null,
            validFrom: productData.validFrom,
            validTo: productData.validTo,
            selfServiceMoveCancelDisabled: !!productData.selfServiceMoveCancelDisabled,
            pricing: pricingData,
            resourceIds: resourceMode === 'resources' ? selectedResources : [],
            orderedResourceIds: resourceMode === 'orderedResources' 
              ? [...selectedOrderedResources].sort((a, b) => a.orderPosition - b.orderPosition).map(r => r.resourceId)
              : [],
          },
        });

        if (!productResponse || !productResponse._id) {
          throw new Error('Product creation failed');
        }

        productId = productResponse._id;
        setCreatedProductId(productId);
      }

      // Load saved product (pricing list IDs are needed for availability assignments)
      const savedProduct = await sendRequest({
        url: `/api/v1/product/${productId}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
      const savedPricing = Array.isArray(savedProduct?.pricing) ? savedProduct.pricing : [];

      // Update entertainments with resource assignments
      if ((resourceMode === 'resources' && selectedResources.length > 0) ||
          (resourceMode === 'orderedResources' && selectedOrderedResources.length > 0)) {
        for (const entertainmentId of selectedEntertainments) {
          try {
            if (resourceMode === 'resources') {
              // Add resourceIds to entertainment
              await sendRequest({
                url: `/api/v1/entertainment/${entertainmentId}`,
                method: 'PATCH',
                headers: authCtx.getAuthHeaders(),
                body: {
                  resourceIds: selectedResources,
                },
              });
            } else if (resourceMode === 'orderedResources') {
              // Add orderedResources to entertainment
              const sortedResources = [...selectedOrderedResources].sort((a, b) => a.orderPosition - b.orderPosition);
              const existingEntertainment = entertainments.find(e => e._id === entertainmentId);
              const existingOrderedResources =
                existingEntertainment && typeof existingEntertainment === 'object'
                  ? existingEntertainment.orderedResources || {}
                  : {};
              await sendRequest({
                url: `/api/v1/entertainment/${entertainmentId}`,
                method: 'PATCH',
                headers: authCtx.getAuthHeaders(),
                body: {
                  orderedResources: {
                    ...existingOrderedResources,
                    resources: sortedResources.map(r => ({
                      resourceId: r.resourceId,
                      orderPosition: r.orderPosition,
                      resourceDurationMinutes: Number(r?.resourceDurationMinutes || 0),
                    })),
                  },
                },
              });
            }
          } catch (err) {
            console.error(`Error updating entertainment ${entertainmentId}:`, err);
          }
        }
      }

      // Update availabilities: legacy assignments + entertainmentProductRelation (preferred for lookups)
      for (const availabilityId of selectedAvailabilities) {
        let currentAv = availabilities.find(a => a._id === availabilityId);
        if (!currentAv) continue;

        const draftAv = availabilityEdits?.[availabilityId];
        const selectedPricingIdxRaw = availabilityPricingSelection?.[availabilityId];
        const selectedPricingIdx = Number.isFinite(selectedPricingIdxRaw) ? selectedPricingIdxRaw : 0;
        const selectedPricing = savedPricing[selectedPricingIdx] || savedPricing[0];
        const selectedPricingId = selectedPricing?._id;

        const entIds = normalizeBookingWidgetEntertainmentIds(
          Array.isArray(draftAv?.assignedEntertainmentIds) && draftAv.assignedEntertainmentIds.length > 0
            ? draftAv.assignedEntertainmentIds
            : selectedEntertainments
        );

        if (Array.isArray(draftAv?.assignedEntertainmentIds)) {
          try {
            const avPatch = await sendRequest({
              url: `/api/v1/availability/${availabilityId}`,
              method: 'PATCH',
              headers: authCtx.getAuthHeaders(),
              body: {
                assignedEntertainments: buildAssignedEntertainmentsForApi(
                  draftAv.assignedEntertainmentIds,
                  entertainments
                ),
              },
            });
            if (avPatch && avPatch._id) {
              currentAv = avPatch;
              setAvailabilities(prev => prev.map(a => (a._id === availabilityId ? avPatch : a)));
            }
          } catch (err) {
            console.error(`Error updating availability entertainments ${availabilityId}:`, err);
          }
        }

        const existingProducts = Array.isArray(currentAv.assignedProducts) ? currentAv.assignedProducts : [];
        const alreadyAssigned = existingProducts.some(p => String(p.productId) === String(productId));

        if (!alreadyAssigned && selectedPricingId) {
          try {
            const avAfterProduct = await sendRequest({
              url: `/api/v1/availability/${availabilityId}/assignedProduct`,
              method: 'POST',
              headers: authCtx.getAuthHeaders(),
              body: {
                productId: productId,
                name: productData.name,
                pricelistId: selectedPricingId,
                pricelistName: selectedPricing?.list || pricingLists[selectedPricingIdx]?.list || 'Standard',
                active: true,
              },
            });
            if (avAfterProduct && avAfterProduct._id) {
              currentAv = avAfterProduct;
              setAvailabilities(prev => prev.map(a => (a._id === availabilityId ? avAfterProduct : a)));
            }
          } catch (err) {
            console.error(`Error adding assigned product to availability ${availabilityId}:`, err);
          }
        }

        if (entIds.length > 0 && selectedPricingId) {
          try {
            const rels = Array.isArray(currentAv.entertainmentProductRelations)
              ? currentAv.entertainmentProductRelations
              : [];
            const match = rels.find(
              (r) =>
                String(r.productId) === String(productId) &&
                String(r.pricelistId) === String(selectedPricingId)
            );
            // Per-relation time-slot override (empty array clears any override).
            const relationHours = (Array.isArray(draftAv?.relationHoursOverride)
              ? draftAv.relationHoursOverride
              : []
            )
              .map(h => ({
                dayCode: (Array.isArray(h.dayCode) ? h.dayCode : [])
                  .map(n => Number(n))
                  .filter(n => Number.isFinite(n)),
                start: h.start,
                end: h.end,
                durationsMinutes: (Array.isArray(h.durationsMinutes) ? h.durationsMinutes : [])
                  .map(n => Number(n))
                  .filter(n => Number.isFinite(n) && n > 0),
              }))
              .filter(h => h.dayCode.length > 0 && h.start && h.end);
            let avAfterRel = null;
            if (match && match._id) {
              avAfterRel = await sendRequest({
                url: `/api/v1/availability/${availabilityId}/entertainmentProductRelation/${match._id}`,
                method: 'PATCH',
                headers: authCtx.getAuthHeaders(),
                body: { entertainmentIds: entIds, hours: relationHours },
              });
            } else {
              avAfterRel = await sendRequest({
                url: `/api/v1/availability/${availabilityId}/entertainmentProductRelation`,
                method: 'POST',
                headers: authCtx.getAuthHeaders(),
                body: {
                  entertainmentIds: entIds,
                  productId,
                  pricelistId: selectedPricingId,
                  active: true,
                  hours: relationHours,
                },
              });
            }
            if (avAfterRel && avAfterRel._id) {
              setAvailabilities(prev => prev.map(a => (a._id === availabilityId ? avAfterRel : a)));
            }
          } catch (err) {
            console.error(`Error upserting entertainment product relation ${availabilityId}:`, err);
          }
        }
      }

      // Handle booking widget - either use selected or create one or more new widgets
      const entPool = buildWidgetEntertainmentPool({
        selectedEntertainments,
        selectedAvailabilities,
        availabilities,
        productId,
      });

      const bookingWidgetIdsCreatedThisRun = [];

      if (selectedBookingWidget) {
        setCreatedBookingWidgetIds([selectedBookingWidget]);
      } else if (createNewWidget) {
        const rowsToCreate = newBookingWidgets.filter(w => w.name.trim());
        if (rowsToCreate.length > 0 && entPool.length === 0) {
          toast.error(
            'Entertainment-Pool ist leer: Bitte im ersten Schritt Entertainments auswählen, bevor Booking-Widgets angelegt werden.'
          );
          setIsSaving(false);
          return;
        }
        const createdNewIds = [];
        for (const row of rowsToCreate) {
          const allowedPricingIds = Array.isArray(row.allowedPricingIndices)
            ? row.allowedPricingIndices
                .map(index => savedPricing[Number(index)]?._id)
                .filter(Boolean)
            : [];

          const rowEntIds = filterEntertainmentIdsToPool(row.linkedEntertainmentIds, entPool);
          if (entPool.length > 0 && rowEntIds.length === 0) {
            toast.error('Bitte pro neuem Booking-Widget mindestens ein Entertainment aus dem Pool auswählen.');
            setIsSaving(false);
            return;
          }

          const widgetResponse = await sendRequest({
            url: '/api/v1/bookingWidget',
            method: 'POST',
            headers: authCtx.getAuthHeaders(),
            body: {
              name: row.name.trim(),
              description: row.description,
              linkedProduct: productId,
              linkedEntertainments: rowEntIds,
              allowedPricingIds,
            },
          });

          if (widgetResponse && widgetResponse._id) {
            createdNewIds.push(widgetResponse._id);
            bookingWidgetIdsCreatedThisRun.push(widgetResponse._id);
            setBookingWidgets(prev => [...prev, widgetResponse]);
          }
        }
        if (createdNewIds.length > 0) {
          setCreatedBookingWidgetIds(() => [
            ...new Set([...existingLinkedBookingWidgetIds, ...createdNewIds]),
          ]);
          setExistingLinkedBookingWidgetIds(prev => [...new Set([...prev, ...createdNewIds])]);
        } else if (isEditMode && existingLinkedBookingWidgetIds.length > 0) {
          setCreatedBookingWidgetIds([...existingLinkedBookingWidgetIds]);
        }
      } else if (isEditMode && existingLinkedBookingWidgetIds.length > 0) {
        setCreatedBookingWidgetIds([...existingLinkedBookingWidgetIds]);
      }

      const createdRunSet = new Set(bookingWidgetIdsCreatedThisRun.map((id) => String(id)));
      const widgetIdsToPatch = new Set();
      for (const w of bookingWidgets) {
        if (w?._id && String(w.linkedProduct) === String(productId)) {
          widgetIdsToPatch.add(String(w._id));
        }
      }
      existingLinkedBookingWidgetIds.forEach((id) => id && widgetIdsToPatch.add(String(id)));
      if (selectedBookingWidget) widgetIdsToPatch.add(String(selectedBookingWidget));

      if (entPool.length > 0) {
        for (const wid of widgetIdsToPatch) {
          if (createdRunSet.has(wid)) continue;
          const rawSel =
            widgetEntertainmentSelections[wid] ?? widgetEntertainmentSelections[String(wid)];
          const wDoc = bookingWidgets.find((x) => String(x._id) === String(wid));
          const fallback = wDoc ? normalizeBookingWidgetEntertainmentIds(wDoc.linkedEntertainments) : [];
          const merged = rawSel !== undefined && rawSel !== null ? rawSel : fallback;
          const cleaned = filterEntertainmentIdsToPool(merged, entPool);
          if (cleaned.length === 0) {
            toast.error('Bitte pro Booking-Widget mindestens ein Entertainment aus dem verfügbaren Pool wählen.');
            setIsSaving(false);
            return;
          }
          try {
            await sendRequest({
              url: `/api/v1/bookingWidget/${wid}`,
              method: 'PATCH',
              headers: authCtx.getAuthHeaders(),
              body: { linkedEntertainments: cleaned },
            });
          } catch (err) {
            console.error(`Booking widget ${wid} linkedEntertainments update failed:`, err);
          }
        }
      }

      toast.success(isEditMode ? 'Produkt aktualisiert' : 'Produkt erstellt');
      
      // Dispatch refresh event for EditableList
      window.dispatchEvent(new CustomEvent('editableListRefresh', { detail: { resource: 'product' } }));
      
      if (onSuccess) {
        onSuccess(productId);
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setIdCopied(type);
    setTimeout(() => setIdCopied(null), 2000);
  };

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderBasicStep();
      case 1:
        return renderPricingStep();
      case 2:
        return renderAvailabilityStep();
      case 3:
        return renderWidgetStep();
      default:
        return null;
    }
  };

  // Step 1: Basic data & Entertainment
  const renderBasicStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Produktdaten</Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Interner Name"
            value={productData.internalName}
            onChange={(e) => setProductData(prev => ({ ...prev, internalName: e.target.value }))}
            fullWidth
            required
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Name"
            value={productData.name}
            onChange={(e) => setProductData(prev => ({ ...prev, name: e.target.value }))}
            fullWidth
            required
            size="small"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Beschreibung"
            value={productData.description}
            onChange={(e) => setProductData(prev => ({ ...prev, description: e.target.value }))}
            fullWidth
            multiline
            rows={2}
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Tags (kommagetrennt)"
            value={productData.tags}
            onChange={(e) => setProductData(prev => ({ ...prev, tags: e.target.value }))}
            fullWidth
            size="small"
            placeholder="z.B. karaoke, premium"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Min. Teilnehmer"
            type="number"
            value={productData.minParticipants}
            onChange={(e) => setProductData(prev => ({ ...prev, minParticipants: e.target.value }))}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Max. Teilnehmer"
            type="number"
            value={productData.maxParticipants}
            onChange={(e) => setProductData(prev => ({ ...prev, maxParticipants: e.target.value }))}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Gültig ab"
            type="date"
            value={productData.validFrom}
            onChange={(e) => setProductData(prev => ({ ...prev, validFrom: e.target.value }))}
            fullWidth
            required
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Gültig bis"
            type="date"
            value={productData.validTo}
            onChange={(e) => setProductData(prev => ({ ...prev, validTo: e.target.value }))}
            fullWidth
            required
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={!!productData.selfServiceMoveCancelDisabled}
                onChange={(e) => setProductData(prev => ({ ...prev, selfServiceMoveCancelDisabled: e.target.checked }))}
                color="success"
              />
            }
            label="Verschieben & Stornieren im Selfservice deaktivieren"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Entertainment-Zuordnung</Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={() => setShowEntertainmentCreate(true)}
          size="small"
          disabled={!authCtx.checkRight('entertainment:create')}
        >
          Neues Entertainment
        </Button>
      </Box>

      <FormControl fullWidth size="small">
        <InputLabel>Entertainments auswählen</InputLabel>
        <Select
          multiple
          value={selectedEntertainments}
          onChange={(e) => setSelectedEntertainments(e.target.value)}
          input={<OutlinedInput label="Entertainments auswählen" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((id) => {
                const ent = entertainments.find(e => e._id === id);
                return <Chip key={id} label={ent?.longName || ent?.entertainmentName || id} size="small" />;
              })}
            </Box>
          )}
        >
          {entertainments.map((ent) => (
            <MenuItem key={ent._id} value={ent._id}>
              <Checkbox checked={selectedEntertainments.includes(ent._id)} />
              <ListItemText primary={ent.longName || ent.entertainmentName} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Create Entertainment Dialog */}
      <Collapse in={showEntertainmentCreate} mountOnEnter unmountOnExit>
        <Paper sx={{ p: 2, mt: 2, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>Neues Entertainment erstellen</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Entertainment Name"
                value={newEntertainmentData.entertainmentName}
                onChange={(e) => setNewEntertainmentData(prev => ({ ...prev, entertainmentName: e.target.value }))}
                fullWidth
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Filiale</InputLabel>
                <Select
                  value={newEntertainmentData.branch}
                  onChange={(e) => setNewEntertainmentData(prev => ({ ...prev, branch: e.target.value }))}
                  label="Filiale"
                >
                  {branches.map((b) => (
                    <MenuItem key={b._id} value={b._id}>
                      {b.longName || b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" size="small" onClick={handleCreateEntertainment}>
                  Erstellen
                </Button>
                <Button size="small" onClick={() => setShowEntertainmentCreate(false)}>
                  Abbrechen
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      <Divider sx={{ my: 2 }} />

      {/* Resource Selection */}
      <Typography variant="h6">Ressourcen-Zuordnung</Typography>
      

      <FormControl component="fieldset">
        <RadioGroup
          row
          value={resourceMode}
          onChange={(e) => {
            const newMode = e.target.value;
            setResourceMode(newMode);
            if (newMode === 'resources') {
              setSelectedOrderedResources([]);
            } else if (newMode === 'orderedResources') {
              setSelectedResources([]);
            } else {
              setSelectedResources([]);
              setSelectedOrderedResources([]);
            }
          }}
        >
          <FormControlLabel value="none" control={<Radio />} label="Keine Ressourcen" />
          <FormControlLabel value="resources" control={<Radio />} label="Ressourcen (resourceIds)" />
          <FormControlLabel value="orderedResources" control={<Radio />} label="Geordnete Ressourcen (orderedResourceIds)" />
        </RadioGroup>
      </FormControl>

      {/* Resources dropdown - simple multi-select */}
      <Collapse in={resourceMode === 'resources'} mountOnEnter unmountOnExit>
        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
          <InputLabel>Ressourcen auswählen</InputLabel>
          <Select
            multiple
            value={selectedResources}
            onChange={(e) => setSelectedResources(e.target.value)}
            input={<OutlinedInput label="Ressourcen auswählen" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((id) => {
                  const res = resources.find(r => r._id === id);
                  return <Chip key={id} label={res?.name || id} size="small" />;
                })}
              </Box>
            )}
          >
            {resources.map((res) => (
              <MenuItem key={res._id} value={res._id}>
                <Checkbox checked={selectedResources.includes(res._id)} />
                <ListItemText 
                  primary={res.name} 
                  secondary={`${res.type} | ${res.minPersons}-${res.maxPersons} Pers.`}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Collapse>

      {/* Ordered Resources - with position management */}
      <Collapse in={resourceMode === 'orderedResources'} mountOnEnter unmountOnExit>
        <Box sx={{ mt: 2 }}>
          {/* Add new ordered resource */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} md={8}>
              <FormControl fullWidth size="small">
                <InputLabel>Ressource hinzufügen</InputLabel>
                <Select
                  value=""
                  onChange={(e) => {
                    const resourceId = e.target.value;
                    if (resourceId && !selectedOrderedResources.some(r => r.resourceId === resourceId)) {
                      const maxPosition = selectedOrderedResources.length > 0 
                        ? Math.max(...selectedOrderedResources.map(r => r.orderPosition)) 
                        : 0;
                      setSelectedOrderedResources(prev => [...prev, {
                        resourceId,
                        orderPosition: maxPosition + 10,
                        resourceDurationMinutes: '',
                      }]);
                    }
                  }}
                  label="Ressource hinzufügen"
                >
                  {resources
                    .filter(res => !selectedOrderedResources.some(r => r.resourceId === res._id))
                    .map((res) => (
                      <MenuItem key={res._id} value={res._id}>
                        {res.name} ({res.type} | {res.minPersons}-{res.maxPersons} Pers.)
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* List of selected ordered resources */}
          {selectedOrderedResources.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Ausgewählte geordnete Ressourcen (sortiert nach Position)
              </Typography>
              {[...selectedOrderedResources]
                .sort((a, b) => a.orderPosition - b.orderPosition)
                .map((orderedRes) => {
                  const res = resources.find(r => r._id === orderedRes.resourceId);
                  return (
                    <Box 
                      key={orderedRes.resourceId} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2, 
                        py: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      <TextField
                        label="Position"
                        type="number"
                        value={orderedRes.orderPosition}
                        onChange={(e) => {
                          const newPosition = parseInt(e.target.value, 10) || 0;
                          setSelectedOrderedResources(prev => 
                            prev.map(r => 
                              r.resourceId === orderedRes.resourceId 
                                ? { ...r, orderPosition: newPosition }
                                : r
                            )
                          );
                        }}
                        size="small"
                        sx={{ width: 100 }}
                        InputProps={{ inputProps: { min: 0 } }}
                      />
                      <TextField
                        label="Dauer (Min.)"
                        type="number"
                        value={orderedRes.resourceDurationMinutes}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const next = raw === '' ? '' : parseInt(raw, 10) || 0;
                          setSelectedOrderedResources(prev =>
                            prev.map(r =>
                              r.resourceId === orderedRes.resourceId
                                ? { ...r, resourceDurationMinutes: next }
                                : r
                            )
                          );
                        }}
                        size="small"
                        sx={{ width: 130 }}
                        InputProps={{ inputProps: { min: 1 } }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2">{res?.name || orderedRes.resourceId}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {res?.type} | {res?.minPersons}-{res?.maxPersons} Pers.
                        </Typography>
                      </Box>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => {
                          setSelectedOrderedResources(prev => 
                            prev.filter(r => r.resourceId !== orderedRes.resourceId)
                          );
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })}
            </Paper>
          )}

          {selectedOrderedResources.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Wählen Sie Ressourcen aus dem Dropdown aus, um sie mit einer Position hinzuzufügen.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );

  // Step 2: Pricing
  const renderPricingStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Preislisten</Typography>
        <Button startIcon={<AddIcon />} onClick={addPricingList} size="small">
          Neue Preisliste
        </Button>
      </Box>

      {pricingLists.length === 0 && (
        <Alert severity="info">
          Fügen Sie mindestens eine Preisliste hinzu, um fortzufahren.
        </Alert>
      )}

      {pricingLists.map((pl) => (
        <Paper key={pl.id} sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => setExpandedPricingList(expandedPricingList === pl.id ? null : pl.id)}
              >
                {expandedPricingList === pl.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <TextField
                label="Listenname"
                defaultValue={pl.list}
                onChange={(e) => {
                  const next = e.target.value;
                  pricingListInputsRef.current = {
                    ...(pricingListInputsRef.current || {}),
                    [pl.id]: { ...(pricingListInputsRef.current?.[pl.id] || {}), list: next },
                  };
                }}
                onBlur={(e) => updatePricingList(pl.id, 'list', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
                size="small"
                sx={{ width: 200 }}
              />
              <TextField
                label="Tags"
                defaultValue={pl.tags}
                onChange={(e) => {
                  const next = e.target.value;
                  pricingListInputsRef.current = {
                    ...(pricingListInputsRef.current || {}),
                    [pl.id]: { ...(pricingListInputsRef.current?.[pl.id] || {}), tags: next },
                  };
                }}
                onBlur={(e) => updatePricingList(pl.id, 'tags', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
                size="small"
                sx={{ width: 150 }}
                placeholder="kommagetrennt"
              />
            </Box>
            <IconButton color="error" onClick={() => removePricingList(pl.id)}>
              <DeleteIcon />
            </IconButton>
          </Box>

          <Collapse in={expandedPricingList === pl.id} mountOnEnter unmountOnExit>
            <Divider sx={{ my: 2 }} />
            
            {/* Categories */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">Kategorien</Typography>
                <Button startIcon={<AddIcon />} size="small" onClick={() => addCategory(pl.id)}>
                  Kategorie
                </Button>
              </Box>
              <Grid container spacing={1}>
                {pl.categories.map((cat, idx) => (
                  <Grid item xs={12} md={4} key={cat.id || cat._id || idx}>
                    <Paper variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        label="Name"
                        value={cat.name}
                        onChange={(e) => updateCategory(pl.id, cat.id || cat._id, 'name', e.target.value)}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Kurz"
                        value={cat.shortName}
                        onChange={(e) => updateCategory(pl.id, cat.id || cat._id, 'shortName', e.target.value)}
                        size="small"
                        sx={{ width: 70 }}
                      />
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => removeCategory(pl.id, cat.id || cat._id)}
                        disabled={pl.categories.length <= 1}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Durations */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">Dauern</Typography>
                <Button startIcon={<AddIcon />} size="small" onClick={() => addDuration(pl.id)}>
                  Dauer
                </Button>
              </Box>
              <Grid container spacing={1}>
                {pl.durations.map((dur, idx) => (
                  <Grid item xs={12} md={6} key={dur.id || dur._id || idx}>
                    <Paper variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <TextField
                        label={(String(dur.recommendedDurationUnit || 'Minuten') === 'Stunden') ? 'Stunden' : 'Minuten'}
                        type="number"
                        value={dur.durationMinutes ?? (
                          String(dur.recommendedDurationUnit || 'Minuten') === 'Stunden'
                            ? Math.round(dur.durationMS / (60 * 60000))
                            : Math.round(dur.durationMS / 60000)
                        )}
                        onChange={(e) => updateDuration(pl.id, dur.id || dur._id, 'durationMinutes', e.target.value)}
                        size="small"
                        sx={{ width: 90 }}
                      />
                      <FormControl size="small" sx={{ width: 120 }}>
                        <InputLabel>Einheit</InputLabel>
                        <Select
                          value={dur.recommendedDurationUnit || 'Minuten'}
                          label="Einheit"
                          onChange={(e) => updateDuration(pl.id, dur.id || dur._id, 'recommendedDurationUnit', e.target.value)}
                        >
                          <MenuItem value="Minuten">Minuten</MenuItem>
                          <MenuItem value="Stunden">Stunden</MenuItem>
                        </Select>
                      </FormControl>
                      <Tooltip title="Bestseller">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={dur.isBestseller || false}
                              onChange={(e) => updateDuration(pl.id, dur.id || dur._id, 'isBestseller', e.target.checked)}
                              size="small"
                            />
                          }
                          label="★"
                          sx={{ m: 0 }}
                        />
                      </Tooltip>
                      <Tooltip title="Nur für eingeloggte Benutzer buchbar">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={dur.isUserBookingRestricted || false}
                              onChange={(e) => updateDuration(pl.id, dur.id || dur._id, 'isUserBookingRestricted', e.target.checked)}
                              size="small"
                            />
                          }
                          label="🔒"
                          sx={{ m: 0 }}
                        />
                      </Tooltip>
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => removeDuration(pl.id, dur.id || dur._id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              {pl.durations.length > 0 && pl.categories.length > 0 && (
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={() => generatePriceMatrix(pl.id)}
                  sx={{ mt: 1 }}
                >
                  Preismatrix generieren / aktualisieren
                </Button>
              )}
            </Box>

            {/* Price Matrix */}
            {pl.priceMatrix.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Preismatrix</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Kategorie</TableCell>
                        <TableCell>Dauer</TableCell>
                        <TableCell>Staffelpreise</TableCell>
                        <TableCell width={40}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pl.priceMatrix.map((pm) => {
                        const dur = pl.durations.find(d => d.durationMS === pm.durationMS);
                        const unit = String(dur?.recommendedDurationUnit || 'Minuten');
                        const amount = unit === 'Stunden'
                          ? (pm.durationMS / (60 * 60000))
                          : (pm.durationMS / 60000);
                        const displayAmount = Number.isFinite(amount)
                          ? (Math.abs(amount - Math.round(amount)) < 1e-9 ? String(Math.round(amount)) : String(Math.round(amount * 100) / 100))
                          : '';
                        return (
                          <TableRow key={pm.id}>
                            <TableCell>{pm.category}</TableCell>
                            <TableCell>{dur ? `${displayAmount} ${unit === 'Stunden' ? 'Std' : 'Min'}` : pm.durationMS}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {pm.prices.map((price, idx) => (
                                  <Paper 
                                    key={idx} 
                                    variant="outlined" 
                                    sx={{ p: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}
                                  >
                                    <TextField
                                      label="Ab Pers."
                                      type="number"
                                      value={price.minPersons}
                                      onChange={(e) => updatePriceMatrixEntry(pl.id, pm.id, idx, 'minPersons', e.target.value)}
                                      size="small"
                                      sx={{ width: 80 }}
                                      InputProps={{ inputProps: { min: 1 } }}
                                    />
                                    <TextField
                                      label="Preis €"
                                      type="number"
                                      value={price.price}
                                      onChange={(e) => updatePriceMatrixEntry(pl.id, pm.id, idx, 'price', e.target.value)}
                                      size="small"
                                      sx={{ width: 90 }}
                                      InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                    />
                                    {pm.prices.length > 1 && (
                                      <IconButton 
                                        size="small" 
                                        onClick={() => removeTierPrice(pl.id, pm.id, idx)}
                                      >
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                  </Paper>
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Staffelpreis hinzufügen">
                                <IconButton size="small" onClick={() => addTierPrice(pl.id, pm.id)}>
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Payment Options */}
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Zahlungsarten (kommagetrennt)"
                    value={pl.paymentOptions}
                    onChange={(e) => updatePricingList(pl.id, 'paymentOptions', e.target.value)}
                    size="small"
                    fullWidth
                    placeholder="online, offline"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Online-Zahlungsarten (kommagetrennt)"
                    value={pl.onlinePaymentOptions}
                    onChange={(e) => updatePricingList(pl.id, 'onlinePaymentOptions', e.target.value)}
                    size="small"
                    fullWidth
                    placeholder="card, paypal"
                  />
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      ))}
    </Box>
  );

  // Step 3: Availability & Opening Hours
  const renderAvailabilityStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Availabilities */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Verfügbarkeiten</Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setShowAvailabilityCreate(true)}
            size="small"
            disabled={!authCtx.checkRight('availability:create')}
          >
            Neue Verfügbarkeit
          </Button>
        </Box>

        {selectedEntertainments.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Im ersten Schritt sind keine Entertainments mit dem Produkt verknüpft. Ohne Entertainment-Zuordnung
            funktionieren Online-Buchungen für diese Verfügbarkeit nicht.
          </Alert>
        )}

        <FormControl fullWidth size="small">
          <InputLabel>Verfügbarkeiten auswählen</InputLabel>
          <Select
            multiple
            value={selectedAvailabilities}
            onChange={(e) => setSelectedAvailabilities(e.target.value)}
            input={<OutlinedInput label="Verfügbarkeiten auswählen" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((id) => {
                  const av = availabilities.find(a => a._id === id);
                  return <Chip key={id} label={av?.name || id} size="small" />;
                })}
              </Box>
            )}
          >
            {availabilities.map((av) => (
              <MenuItem key={av._id} value={av._id}>
                <Checkbox checked={selectedAvailabilities.includes(av._id)} />
                <ListItemText 
                  primary={av.name} 
                  secondary={`Priorität ${av.priority} | ${dayjs(av.validFrom).format('DD.MM.YY')} - ${dayjs(av.validTo).format('DD.MM.YY')}`}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Pricing list assignment per availability */}
        {selectedAvailabilities.length > 0 && pricingLists.length > 0 && (
          <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Preisliste pro Verfügbarkeit
            </Typography>
            <Grid container spacing={2}>
              {selectedAvailabilities.map((avId) => {
                const av = availabilities.find(a => a._id === avId);
                const currentIdx = Number.isFinite(availabilityPricingSelection?.[avId])
                  ? availabilityPricingSelection[avId]
                  : 0;
                return (
                  <Grid item xs={12} md={6} key={avId}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {av?.name || avId}
                      </Typography>
                      <FormControl fullWidth size="small">
                        <InputLabel>Preisliste</InputLabel>
                        <Select
                          value={currentIdx}
                          label="Preisliste"
                          onChange={(e) => {
                            const idx = parseInt(e.target.value, 10);
                            setAvailabilityPricingSelection(prev => ({
                              ...(prev || {}),
                              [avId]: Number.isFinite(idx) ? idx : 0,
                            }));
                          }}
                        >
                          {pricingLists.map((pl, idx) => (
                            <MenuItem key={pl.id || `${pl.list}-${idx}`} value={idx}>
                              {pl.list || `Preisliste ${idx + 1}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        )}

        {selectedAvailabilities.length > 0 && pricingLists.length === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Es gibt noch keine Preislisten. Bitte legen Sie im Schritt „Preisfestlegung“ mindestens eine Preisliste an.
          </Alert>
        )}

        {selectedAvailabilities.length > 0 && (
          <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Ausgewählte Verfügbarkeiten bearbeiten
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {selectedAvailabilities.map((avId) => {
                const av = availabilities.find(a => a._id === avId);
                const draft = availabilityEdits?.[avId];
                const isExpanded = expandedAvailabilityEdit === avId;
                const isSavingThis = Boolean(availabilitySavingById?.[avId]);

                if (!draft) {
                  return (
                    <Paper key={avId} variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="body2">
                        {av?.name || avId}
                      </Typography>
                    </Paper>
                  );
                }

                return (
                  <Paper key={avId} variant="outlined" sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => setExpandedAvailabilityEdit(isExpanded ? null : avId)}
                        >
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {av?.name || draft.name || avId}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleUpdateAvailability(avId)}
                        disabled={isSavingThis || !authCtx.checkRight('availability:update')}
                        startIcon={isSavingThis ? <CircularProgress size={16} /> : null}
                      >
                        {isSavingThis ? 'Speichern...' : 'Speichern'}
                      </Button>
                    </Box>

                    <Collapse in={isExpanded} mountOnEnter unmountOnExit>
                      <Divider sx={{ my: 2 }} />
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormControl fullWidth size="small">
                            <InputLabel id={`av-ent-label-${avId}`}>Entertainments zuordnen</InputLabel>
                            <Select
                              labelId={`av-ent-label-${avId}`}
                              multiple
                              value={Array.isArray(draft.assignedEntertainmentIds) ? draft.assignedEntertainmentIds : []}
                              onChange={(e) =>
                                updateAvailabilityEdit(avId, { assignedEntertainmentIds: e.target.value })
                              }
                              input={<OutlinedInput label="Entertainments zuordnen" />}
                              renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {(Array.isArray(selected) ? selected : []).map((id) => {
                                    const ent = entertainments.find((e) => String(e._id) === String(id));
                                    return (
                                      <Chip
                                        key={id}
                                        size="small"
                                        label={ent?.longName || ent?.entertainmentName || String(id)}
                                      />
                                    );
                                  })}
                                </Box>
                              )}
                            >
                              {selectedEntertainments.map((eid) => {
                                const ent = entertainments.find((e) => String(e._id) === String(eid));
                                return (
                                  <MenuItem key={String(eid)} value={eid}>
                                    <Checkbox
                                      checked={(draft.assignedEntertainmentIds || []).some(
                                        (x) => String(x) === String(eid)
                                      )}
                                    />
                                    <ListItemText
                                      primary={ent?.longName || ent?.entertainmentName || String(eid)}
                                    />
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Für das Booking-Widget müssen dieselben Entertainments wie dort verknüpft in dieser
                            Verfügbarkeit stehen (technische Verknüpfung Entertainment + Produkt).
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Name"
                            value={draft.name}
                            onChange={(e) => updateAvailabilityEdit(avId, { name: e.target.value })}
                            fullWidth
                            size="small"
                            required
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Priorität"
                            type="number"
                            value={draft.priority}
                            onChange={(e) => updateAvailabilityEdit(avId, { priority: parseInt(e.target.value, 10) })}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Sondertypen</InputLabel>
                            <Select
                              multiple
                              value={Array.isArray(draft.specialTypes) ? draft.specialTypes : []}
                              onChange={(e) => updateAvailabilityEdit(avId, { specialTypes: e.target.value })}
                              input={<OutlinedInput label="Sondertypen" />}
                              renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {(Array.isArray(selected) ? selected : []).map((v) => (
                                    <Chip
                                      key={v}
                                      label={
                                        v === 'holiday' ? 'Feiertag' :
                                        v === 'vacation' ? 'Urlaub' :
                                        v === 'summerVacation' ? 'Sommerurlaub' :
                                        v === 'bridgeDay' ? 'Brückentag' :
                                        v
                                      }
                                      size="small"
                                    />
                                  ))}
                                </Box>
                              )}
                            >
                              {[
                                { value: 'holiday', label: 'Feiertag' },
                                { value: 'vacation', label: 'Urlaub' },
                                { value: 'summerVacation', label: 'Sommerurlaub' },
                                { value: 'bridgeDay', label: 'Brückentag' },
                              ].map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  <Checkbox checked={(draft.specialTypes || []).includes(opt.value)} />
                                  <ListItemText primary={opt.label} />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(draft.allowOutsideOpeningHours)}
                                onChange={(e) => updateAvailabilityEdit(avId, { allowOutsideOpeningHours: e.target.checked })}
                              />
                            }
                            label="Außerhalb Öffnungszeiten erlauben"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="Gültig von"
                            type="date"
                            value={draft.validFrom}
                            onChange={(e) => updateAvailabilityEdit(avId, { validFrom: e.target.value })}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="Gültig bis"
                            type="date"
                            value={draft.validTo}
                            onChange={(e) => updateAvailabilityEdit(avId, { validTo: e.target.value })}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2">Zeitslots</Typography>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => addAvailabilityEditTimeSlot(avId)}
                            >
                              Zeitslot
                            </Button>
                          </Box>
                          {(draft.hours || []).map((slot, idx) => (
                            <Paper key={idx} variant="outlined" sx={{ p: 1, mb: 1 }}>
                              <Grid container spacing={1} alignItems="center">
                                <Grid item xs={12} md={5}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel>Wochentage</InputLabel>
                                    <Select
                                      multiple
                                      value={Array.isArray(slot.dayCode) ? slot.dayCode : []}
                                      onChange={(e) => updateAvailabilityEditTimeSlot(avId, idx, 'dayCode', e.target.value)}
                                      input={<OutlinedInput label="Wochentage" />}
                                      renderValue={(selected) => selected.map(d => WEEKDAYS.find(w => w.value === d)?.short).join(', ')}
                                    >
                                      {WEEKDAYS.map((day) => (
                                        <MenuItem key={day.value} value={day.value}>
                                          <Checkbox checked={(slot.dayCode || []).includes(day.value)} />
                                          <ListItemText primary={day.label} />
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={5} md={3}>
                                  <TextField
                                    label="Start"
                                    type="time"
                                    value={slot.start}
                                    onChange={(e) => updateAvailabilityEditTimeSlot(avId, idx, 'start', e.target.value)}
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                                <Grid item xs={5} md={3}>
                                  <TextField
                                    label="Ende"
                                    type="time"
                                    value={slot.end}
                                    onChange={(e) => updateAvailabilityEditTimeSlot(avId, idx, 'end', e.target.value)}
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                                <Grid item xs={2} md={1}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => removeAvailabilityEditTimeSlot(avId, idx)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            </Paper>
                          ))}
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2">Zeitslot-Override für dieses Produkt</Typography>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => addRelationOverrideTimeSlot(avId)}
                            >
                              Override-Zeitslot
                            </Button>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Wenn hier Slots gesetzt sind, ersetzen sie für dieses Produkt die Zeitslots oben.
                            Leer lassen, um die Zeitslots oben zu verwenden.
                          </Typography>
                          {(draft.relationHoursOverride || []).map((slot, idx) => (
                            <Paper key={idx} variant="outlined" sx={{ p: 1, mb: 1 }}>
                              <Grid container spacing={1} alignItems="center">
                                <Grid item xs={12} md={5}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel>Wochentage</InputLabel>
                                    <Select
                                      multiple
                                      value={Array.isArray(slot.dayCode) ? slot.dayCode : []}
                                      onChange={(e) => updateRelationOverrideTimeSlot(avId, idx, 'dayCode', e.target.value)}
                                      input={<OutlinedInput label="Wochentage" />}
                                      renderValue={(selected) => selected.map(d => WEEKDAYS.find(w => w.value === d)?.short).join(', ')}
                                    >
                                      {WEEKDAYS.map((day) => (
                                        <MenuItem key={day.value} value={day.value}>
                                          <Checkbox checked={(slot.dayCode || []).includes(day.value)} />
                                          <ListItemText primary={day.label} />
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={5} md={3}>
                                  <TextField
                                    label="Start"
                                    type="time"
                                    value={slot.start}
                                    onChange={(e) => updateRelationOverrideTimeSlot(avId, idx, 'start', e.target.value)}
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                                <Grid item xs={5} md={3}>
                                  <TextField
                                    label="Ende"
                                    type="time"
                                    value={slot.end}
                                    onChange={(e) => updateRelationOverrideTimeSlot(avId, idx, 'end', e.target.value)}
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                                <Grid item xs={2} md={1}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => removeRelationOverrideTimeSlot(avId, idx)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Grid>
                                <Grid item xs={12}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel>Durations (leer = alle)</InputLabel>
                                    <Select
                                      multiple
                                      value={Array.isArray(slot.durationsMinutes) ? slot.durationsMinutes : []}
                                      onChange={(e) => updateRelationOverrideTimeSlot(avId, idx, 'durationsMinutes', e.target.value)}
                                      input={<OutlinedInput label="Durations (leer = alle)" />}
                                      renderValue={(selected) => (selected.length ? selected.map(m => `${m} Min`).join(', ') : 'Alle')}
                                    >
                                      {(() => {
                                        const plIdx = Number.isFinite(availabilityPricingSelection?.[avId])
                                          ? availabilityPricingSelection[avId]
                                          : 0;
                                        const pl = pricingLists[plIdx] || pricingLists[0];
                                        const durs = Array.isArray(pl?.durations) ? pl.durations : [];
                                        const seen = new Set();
                                        const minutes = durs
                                          .map(d => Math.round((d.durationMS || 0) / 60000))
                                          .filter(m => m > 0 && !seen.has(m) && (seen.add(m), true))
                                          .sort((a, b) => a - b);
                                        return minutes.map(m => (
                                          <MenuItem key={m} value={m}>
                                            <Checkbox checked={(slot.durationsMinutes || []).includes(m)} />
                                            <ListItemText primary={`${m} Min`} />
                                          </MenuItem>
                                        ));
                                      })()}
                                    </Select>
                                  </FormControl>
                                </Grid>
                              </Grid>
                            </Paper>
                          ))}
                        </Grid>
                      </Grid>
                    </Collapse>
                  </Paper>
                );
              })}
            </Box>
          </Paper>
        )}

        {/* Create Availability Form */}
        <Collapse in={showAvailabilityCreate} mountOnEnter unmountOnExit>
          <Paper ref={availabilityCreateRef} sx={{ p: 2, mt: 2, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Neue Verfügbarkeit erstellen</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Name"
                  value={newAvailabilityData.name}
                  onChange={(e) => setNewAvailabilityData(prev => ({ ...prev, name: e.target.value }))}
                  fullWidth
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Priorität"
                  type="number"
                  value={newAvailabilityData.priority}
                  onChange={(e) => setNewAvailabilityData(prev => ({ ...prev, priority: parseInt(e.target.value, 10) }))}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small" disabled={selectedEntertainments.length === 0}>
                  <InputLabel id="new-av-ent-label">Entertainments zuordnen</InputLabel>
                  <Select
                    labelId="new-av-ent-label"
                    multiple
                    value={normalizeBookingWidgetEntertainmentIds(newAvailabilityData.assignedEntertainmentIds)}
                    onChange={(e) =>
                      setNewAvailabilityData((prev) => ({ ...prev, assignedEntertainmentIds: e.target.value }))
                    }
                    input={<OutlinedInput label="Entertainments zuordnen" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(Array.isArray(selected) ? selected : []).map((id) => {
                          const ent = entertainments.find((e) => String(e._id) === String(id));
                          return (
                            <Chip
                              key={String(id)}
                              size="small"
                              label={ent?.longName || ent?.entertainmentName || String(id)}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {selectedEntertainments.map((eid) => {
                      const ent = entertainments.find((e) => String(e._id) === String(eid));
                      const selectedIds = normalizeBookingWidgetEntertainmentIds(
                        newAvailabilityData.assignedEntertainmentIds
                      );
                      return (
                        <MenuItem key={String(eid)} value={eid}>
                          <Checkbox checked={selectedIds.some((x) => String(x) === String(eid))} />
                          <ListItemText
                            primary={ent?.longName || ent?.entertainmentName || String(eid)}
                          />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Standard: alle Entertainments aus Schritt 1. Ohne Zuordnung ist die Verfügbarkeit für Buchungen
                  nicht nutzbar.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sondertypen</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(newAvailabilityData.specialTypes) ? newAvailabilityData.specialTypes : []}
                    onChange={(e) => setNewAvailabilityData(prev => ({ ...prev, specialTypes: e.target.value }))}
                    input={<OutlinedInput label="Sondertypen" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(Array.isArray(selected) ? selected : []).map((v) => (
                          <Chip
                            key={v}
                            label={
                              v === 'holiday' ? 'Feiertag' :
                              v === 'vacation' ? 'Urlaub' :
                              v === 'summerVacation' ? 'Sommerurlaub' :
                              v === 'bridgeDay' ? 'Brückentag' :
                              v
                            }
                            size="small"
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {[
                      { value: 'holiday', label: 'Feiertag' },
                      { value: 'vacation', label: 'Urlaub' },
                      { value: 'summerVacation', label: 'Sommerurlaub' },
                      { value: 'bridgeDay', label: 'Brückentag' },
                    ].map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Checkbox checked={(newAvailabilityData.specialTypes || []).includes(opt.value)} />
                        <ListItemText primary={opt.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(newAvailabilityData.allowOutsideOpeningHours)}
                      onChange={(e) => setNewAvailabilityData(prev => ({ ...prev, allowOutsideOpeningHours: e.target.checked }))}
                    />
                  }
                  label="Außerhalb Öffnungszeiten erlauben"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Gültig von"
                  type="date"
                  value={newAvailabilityData.validFrom}
                  onChange={(e) => setNewAvailabilityData(prev => ({ ...prev, validFrom: e.target.value }))}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Gültig bis"
                  type="date"
                  value={newAvailabilityData.validTo}
                  onChange={(e) => setNewAvailabilityData(prev => ({ ...prev, validTo: e.target.value }))}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2">Zeitslots</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => addTimeSlot('availability')}>
                    Zeitslot
                  </Button>
                </Box>
                {newAvailabilityData.hours.map((slot, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 1, mb: 1 }}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12} md={5}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Wochentage</InputLabel>
                          <Select
                            multiple
                            value={slot.dayCode}
                            onChange={(e) => updateTimeSlot('availability', idx, 'dayCode', e.target.value)}
                            input={<OutlinedInput label="Wochentage" />}
                            renderValue={(selected) => selected.map(d => WEEKDAYS.find(w => w.value === d)?.short).join(', ')}
                          >
                            {WEEKDAYS.map((day) => (
                              <MenuItem key={day.value} value={day.value}>
                                <Checkbox checked={slot.dayCode.includes(day.value)} />
                                <ListItemText primary={day.label} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={5} md={3}>
                        <TextField
                          label="Start"
                          type="time"
                          value={slot.start}
                          onChange={(e) => updateTimeSlot('availability', idx, 'start', e.target.value)}
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={5} md={3}>
                        <TextField
                          label="Ende"
                          type="time"
                          value={slot.end}
                          onChange={(e) => updateTimeSlot('availability', idx, 'end', e.target.value)}
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={2} md={1}>
                        <IconButton size="small" color="error" onClick={() => removeTimeSlot('availability', idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="contained" size="small" onClick={handleCreateAvailability}>
                    Erstellen
                  </Button>
                  <Button size="small" onClick={() => setShowAvailabilityCreate(false)}>
                    Abbrechen
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>
      </Box>

      <Divider />

      {/* Opening Hours */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Öffnungszeiten</Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setShowOpeningHourCreate(true)}
            size="small"
            disabled={!authCtx.checkRight('openingHour:create')}
          >
            Neue Öffnungszeit
          </Button>
        </Box>

        <FormControl fullWidth size="small">
          <InputLabel>Öffnungszeiten auswählen</InputLabel>
          <Select
            multiple
            value={selectedOpeningHours}
            onChange={(e) => setSelectedOpeningHours(e.target.value)}
            input={<OutlinedInput label="Öffnungszeiten auswählen" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((id) => {
                  const oh = openingHours.find(o => o._id === id);
                  return <Chip key={id} label={oh?.name || id} size="small" />;
                })}
              </Box>
            )}
          >
            {openingHours.map((oh) => (
              <MenuItem key={oh._id} value={oh._id}>
                <Checkbox checked={selectedOpeningHours.includes(oh._id)} />
                <ListItemText 
                  primary={oh.name} 
                  secondary={`Priorität ${oh.priority} | ${dayjs(oh.validFrom).format('DD.MM.YY')} - ${dayjs(oh.validTo).format('DD.MM.YY')}`}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Create Opening Hour Form */}
        <Collapse in={showOpeningHourCreate} mountOnEnter unmountOnExit>
          <Paper sx={{ p: 2, mt: 2, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Neue Öffnungszeit erstellen</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Name"
                  value={newOpeningHourData.name}
                  onChange={(e) => setNewOpeningHourData(prev => ({ ...prev, name: e.target.value }))}
                  fullWidth
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Priorität"
                  type="number"
                  value={newOpeningHourData.priority}
                  onChange={(e) => setNewOpeningHourData(prev => ({ ...prev, priority: parseInt(e.target.value, 10) }))}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Gültig von"
                  type="date"
                  value={newOpeningHourData.validFrom}
                  onChange={(e) => setNewOpeningHourData(prev => ({ ...prev, validFrom: e.target.value }))}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Gültig bis"
                  type="date"
                  value={newOpeningHourData.validTo}
                  onChange={(e) => setNewOpeningHourData(prev => ({ ...prev, validTo: e.target.value }))}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2">Zeitslots</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => addTimeSlot('openingHour')}>
                    Zeitslot
                  </Button>
                </Box>
                {newOpeningHourData.hours.map((slot, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 1, mb: 1 }}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12} md={5}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Wochentage</InputLabel>
                          <Select
                            multiple
                            value={slot.dayCode}
                            onChange={(e) => updateTimeSlot('openingHour', idx, 'dayCode', e.target.value)}
                            input={<OutlinedInput label="Wochentage" />}
                            renderValue={(selected) => selected.map(d => WEEKDAYS.find(w => w.value === d)?.short).join(', ')}
                          >
                            {WEEKDAYS.map((day) => (
                              <MenuItem key={day.value} value={day.value}>
                                <Checkbox checked={slot.dayCode.includes(day.value)} />
                                <ListItemText primary={day.label} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={5} md={3}>
                        <TextField
                          label="Start"
                          type="time"
                          value={slot.start}
                          onChange={(e) => updateTimeSlot('openingHour', idx, 'start', e.target.value)}
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={5} md={3}>
                        <TextField
                          label="Ende"
                          type="time"
                          value={slot.end}
                          onChange={(e) => updateTimeSlot('openingHour', idx, 'end', e.target.value)}
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={2} md={1}>
                        <IconButton size="small" color="error" onClick={() => removeTimeSlot('openingHour', idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="contained" size="small" onClick={handleCreateOpeningHour}>
                    Erstellen
                  </Button>
                  <Button size="small" onClick={() => setShowOpeningHourCreate(false)}>
                    Abbrechen
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>
      </Box>

    </Box>
  );

  const updateNewBookingWidget = (localId, patch) => {
    setNewBookingWidgets(prev => prev.map(r => (r.localId === localId ? { ...r, ...patch } : r)));
  };

  const updateWidgetEntertainmentSelection = (widgetId, ids) => {
    const asStrings = Array.isArray(ids) ? ids.map((id) => String(id)) : [];
    setWidgetEntertainmentSelections((prev) => ({
      ...prev,
      [String(widgetId)]: asStrings,
    }));
  };

  const widgetIdsToDisplay =
    createdBookingWidgetIds.length > 0
      ? createdBookingWidgetIds
      : existingLinkedBookingWidgetIds.length > 0
        ? existingLinkedBookingWidgetIds
        : selectedBookingWidget
          ? [selectedBookingWidget]
          : [];

  // Step 4: Booking Widget
  const renderWidgetStep = () => {
    const productLinkedWidgets = productIdForWidgetPool
      ? bookingWidgets.filter((w) => w && String(w.linkedProduct) === String(productIdForWidgetPool))
      : [];

    return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Entertainment-Pool für Booking-Widgets
        </Typography>
        {widgetEntertainmentPoolIds.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {widgetEntertainmentPoolIds.map((id) => {
              const ent = entertainments.find((e) => String(e._id) === String(id));
              return (
                <Chip
                  key={String(id)}
                  size="small"
                  label={ent?.longName || ent?.entertainmentName || String(id)}
                />
              );
            })}
          </Box>
        ) : (
          <Alert severity="warning">Pool ist leer: Bitte im ersten Schritt Entertainments auswählen.</Alert>
        )}
      </Paper>

      {productLinkedWidgets.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Entertainments pro bestehendem Booking-Widget
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {productLinkedWidgets.map((w) => {
              const wid = String(w._id);
              const sel =
                widgetEntertainmentSelections[wid] ??
                normalizeBookingWidgetEntertainmentIds(w.linkedEntertainments);
              const valueArr = (Array.isArray(sel) ? sel : []).map(String);
              return (
                <Box key={wid}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {w.name || wid}
                  </Typography>
                  <FormControl fullWidth size="small" disabled={widgetEntertainmentPoolIds.length === 0}>
                    <InputLabel>Entertainments</InputLabel>
                    <Select
                      multiple
                      value={valueArr}
                      onChange={(e) => {
                        const v =
                          typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                        updateWidgetEntertainmentSelection(wid, v);
                      }}
                      input={<OutlinedInput label="Entertainments" />}
                      renderValue={(selected) => {
                        if (!Array.isArray(selected) || selected.length === 0) return '—';
                        return selected
                          .map((id) => {
                            const ent = entertainments.find((e) => String(e._id) === String(id));
                            return ent?.longName || ent?.entertainmentName || id;
                          })
                          .join(', ');
                      }}
                    >
                      {widgetEntertainmentPoolIds.map((entId) => {
                        const eid = String(entId);
                        const ent = entertainments.find((e) => String(e._id) === eid);
                        return (
                          <MenuItem key={`${wid}-${eid}`} value={eid}>
                            <Checkbox checked={valueArr.includes(eid)} />
                            <ListItemText primary={ent?.longName || ent?.entertainmentName || eid} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      {/* Select existing booking widget */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Bestehendes Booking Widget auswählen</Typography>
        
        <FormControl fullWidth size="small">
          <InputLabel>Booking Widget</InputLabel>
          <Select
            value={selectedBookingWidget}
            onChange={(e) => {
              setSelectedBookingWidget(e.target.value);
              if (e.target.value) {
                setCreateNewWidget(false);
              }
            }}
            label="Booking Widget"
            disabled={createNewWidget}
          >
            <MenuItem value="">
              <em>Kein Widget auswählen</em>
            </MenuItem>
            {bookingWidgets.map((widget) => (
              <MenuItem key={widget._id} value={widget._id}>
                {widget.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Show selected widget ID for copying */}
        {selectedBookingWidget && !createNewWidget && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Widget-ID zum Kopieren:
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              bgcolor: 'action.hover', 
              px: 2, 
              py: 1, 
              borderRadius: 1,
              fontFamily: 'monospace',
            }}>
              <Typography variant="body2" sx={{ flexGrow: 1 }}>{selectedBookingWidget}</Typography>
              <IconButton 
                size="small" 
                onClick={() => copyToClipboard(selectedBookingWidget, `widget-${selectedBookingWidget}`)}
              >
                {idCopied === `widget-${selectedBookingWidget}` ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
              </IconButton>
            </Box>
          </Box>
        )}

        {bookingWidgets.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Keine bestehenden Booking Widgets gefunden. Sie können unten ein neues erstellen.
          </Alert>
        )}
      </Paper>

      {/* Divider */}
      <Divider>
        <Typography variant="body2" color="text.secondary">oder</Typography>
      </Divider>

      {/* Create new widget(s) */}
      <FormControlLabel
        control={
          <Switch
            checked={createNewWidget}
            onChange={(e) => {
              const checked = e.target.checked;
              setCreateNewWidget(checked);
              if (checked) {
                setSelectedBookingWidget('');
                setNewBookingWidgets((prev) =>
                  prev.length > 0
                    ? prev
                    : [
                        {
                          ...createEmptyNewBookingWidgetRow(),
                          linkedEntertainmentIds: [...widgetEntertainmentPoolIds],
                        },
                      ]
                );
              }
            }}
            disabled={!authCtx.checkRight('bookingWidget:create')}
          />
        }
        label="Neue Booking Widgets erstellen"
      />

      <Collapse in={createNewWidget} mountOnEnter unmountOnExit>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {newBookingWidgets.map((row, rowIndex) => (
            <Paper key={row.localId} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Neues Booking Widget {newBookingWidgets.length > 1 ? `(${rowIndex + 1})` : ''}
                </Typography>
                <Tooltip title="Zeile entfernen">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={newBookingWidgets.length <= 1}
                      onClick={() =>
                        setNewBookingWidgets(prev => prev.filter(r => r.localId !== row.localId))
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Widget Name"
                    value={row.name}
                    onChange={(e) => updateNewBookingWidget(row.localId, { name: e.target.value })}
                    fullWidth
                    size="small"
                    placeholder={productData.name ? `${productData.name} Widget` : 'Widget Name'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Beschreibung"
                    value={row.description}
                    onChange={(e) => updateNewBookingWidget(row.localId, { description: e.target.value })}
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small" disabled={widgetEntertainmentPoolIds.length === 0}>
                    <InputLabel>Entertainments für dieses Widget</InputLabel>
                    <Select
                      multiple
                      value={(row.linkedEntertainmentIds || []).map(String)}
                      onChange={(e) => {
                        const v =
                          typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                        updateNewBookingWidget(row.localId, { linkedEntertainmentIds: v });
                      }}
                      input={<OutlinedInput label="Entertainments für dieses Widget" />}
                      renderValue={(selected) => {
                        if (!Array.isArray(selected) || selected.length === 0) return '—';
                        return selected
                          .map((id) => {
                            const ent = entertainments.find((e) => String(e._id) === String(id));
                            return ent?.longName || ent?.entertainmentName || id;
                          })
                          .join(', ');
                      }}
                    >
                      {widgetEntertainmentPoolIds.map((entId) => {
                        const eid = String(entId);
                        const ent = entertainments.find((e) => String(e._id) === eid);
                        return (
                          <MenuItem key={`${row.localId}-ent-${eid}`} value={eid}>
                            <Checkbox
                              checked={(row.linkedEntertainmentIds || []).map(String).includes(eid)}
                            />
                            <ListItemText primary={ent?.longName || ent?.entertainmentName || eid} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Erlaubte Preislisten</InputLabel>
                    <Select
                      multiple
                      value={row.allowedPricingIndices}
                      onChange={(e) =>
                        updateNewBookingWidget(row.localId, {
                          allowedPricingIndices:
                            typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
                        })
                      }
                      input={<OutlinedInput label="Erlaubte Preislisten" />}
                      renderValue={(selected) => {
                        if (!Array.isArray(selected) || selected.length === 0) {
                          return 'Alle Preislisten';
                        }
                        return selected
                          .map(index => pricingLists[Number(index)]?.list)
                          .filter(Boolean)
                          .join(', ');
                      }}
                      disabled={pricingLists.length === 0}
                    >
                      {pricingLists.map((pricing, index) => (
                        <MenuItem key={`${row.localId}-${pricing.list || 'pricing'}-${index}`} value={String(index)}>
                          <Checkbox checked={row.allowedPricingIndices.includes(String(index))} />
                          <ListItemText primary={pricing.list || `Preisliste ${index + 1}`} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          ))}
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() =>
              setNewBookingWidgets((prev) => [
                ...prev,
                { ...createEmptyNewBookingWidgetRow(), linkedEntertainmentIds: [...widgetEntertainmentPoolIds] },
              ])
            }
            sx={{ alignSelf: 'flex-start' }}
          >
            Weiteres Booking Widget hinzufügen
          </Button>
          <Alert severity="info">
            Jedes Widget wird mit dem Produkt verknüpft; die Entertainment-Auswahl pro Widget bezieht sich auf den Pool
            oben. Bleibt die Preislisten-Auswahl leer, sind alle Preislisten des Produkts erlaubt. Zeilen ohne Namen
            werden beim Speichern übersprungen.
          </Alert>
        </Box>
      </Collapse>

      {/* Show Booking Widget IDs (existing / after save) */}
      {widgetIdsToDisplay.length > 0 && (
        <Paper sx={{ p: 2, mt: 2, bgcolor: 'success.dark', color: 'white' }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {widgetIdsToDisplay.length > 1 ? 'Booking Widget-IDs:' : 'Booking Widget-ID:'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {widgetIdsToDisplay.map((wid) => (
              <Box
                key={wid}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: theme =>
                    alpha(
                      theme.palette.common.white,
                      theme.palette.mode === 'light' ? 0.22 : 0.1
                    ),
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                }}
              >
                <Typography variant="body2" sx={{ flexGrow: 1, wordBreak: 'break-all' }}>{wid}</Typography>
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(wid, `widget-${wid}`)}
                  sx={{ color: 'white', ml: 1, flexShrink: 0 }}
                >
                  {idCopied === `widget-${wid}` ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                </IconButton>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Summary */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Zusammenfassung</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Produktname</Typography>
            <Typography variant="body1">{productData.name || '—'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Interner Name</Typography>
            <Typography variant="body1">{productData.internalName || '—'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Entertainments</Typography>
            <Typography variant="body1">
              {selectedEntertainments.length > 0 
                ? selectedEntertainments.map(id => {
                    const ent = entertainments.find(e => e._id === id);
                    return ent?.longName || ent?.entertainmentName || id;
                  }).join(', ')
                : '—'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Preislisten</Typography>
            <Typography variant="body1">
              {pricingLists.length > 0 ? pricingLists.map(pl => pl.list).join(', ') : '—'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Neue Widgets (Preislisten)</Typography>
            <Typography variant="body1">
              {createNewWidget
                ? (() => {
                    const named = newBookingWidgets.filter(w => w.name.trim());
                    if (named.length === 0) {
                      return 'Keine benannten Widgets';
                    }
                    return named
                      .map((w) => {
                        const lists = w.allowedPricingIndices.length > 0
                          ? w.allowedPricingIndices
                              .map(index => pricingLists[Number(index)]?.list)
                              .filter(Boolean)
                              .join(', ')
                          : 'Alle Preislisten';
                        return `${w.name.trim()}: ${lists}`;
                      })
                      .join(' · ');
                  })()
                : '—'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Verfügbarkeiten</Typography>
            <Typography variant="body1">
              {selectedAvailabilities.length > 0 
                ? selectedAvailabilities.map(id => {
                    const av = availabilities.find(a => a._id === id);
                    return av?.name || id;
                  }).join(', ')
                : '—'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Öffnungszeiten</Typography>
            <Typography variant="body1">
              {selectedOpeningHours.length > 0 
                ? selectedOpeningHours.map(id => {
                    const oh = openingHours.find(o => o._id === id);
                    return oh?.name || id;
                  }).join(', ')
                : '—'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              {resourceMode === 'resources' ? 'Ressourcen' : resourceMode === 'orderedResources' ? 'Geordnete Ressourcen' : 'Ressourcen'}
            </Typography>
            <Typography variant="body1">
              {resourceMode === 'resources' && selectedResources.length > 0 
                ? selectedResources.map(id => {
                    const res = resources.find(r => r._id === id);
                    return res?.name || id;
                  }).join(', ')
                : resourceMode === 'orderedResources' && selectedOrderedResources.length > 0 
                  ? [...selectedOrderedResources]
                      .sort((a, b) => a.orderPosition - b.orderPosition)
                      .map(ordRes => {
                        const res = resources.find(r => r._id === ordRes.resourceId);
                        const dur = Number(ordRes?.resourceDurationMinutes || 0);
                        const durText = Number.isFinite(dur) && dur > 0 ? `, ${dur} Min.` : '';
                        return `${res?.name || ordRes.resourceId} (Pos. ${ordRes.orderPosition}${durText})`;
                      })
                      .join(', ')
                  : '—'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
    );
  };

  // Missing rights warning
  if (missingRights.length > 0 && !isInitializing) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Fehlende Berechtigungen</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mt: 1 }}>
            Sie haben nicht alle erforderlichen Rechte, um das Produkt-Widget zu verwenden.
            <Box component="ul" sx={{ mt: 1, mb: 0 }}>
              {missingRights.map(right => (
                <li key={right}><code>{right}</code></li>
              ))}
            </Box>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Schließen</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      {/* Close Confirmation Dialog */}
      <Dialog 
        open={showCloseConfirm} 
        onClose={() => setShowCloseConfirm(false)}
        maxWidth="xs"
      >
        <DialogTitle>Wizard verlassen?</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie den Wizard wirklich verlassen? Alle nicht gespeicherten Eingaben gehen verloren.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCloseConfirm(false)}>
            Weiter bearbeiten
          </Button>
          <Button onClick={handleClose} color="error" variant="contained">
            Ja, verlassen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Wizard Dialog */}
      <Dialog 
        open={open} 
        onClose={requestClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{ sx: { minHeight: '80vh' } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {isEditMode ? 'Produkt bearbeiten' : 'Neues Produkt erstellen'}
            </Typography>
            <IconButton onClick={requestClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

      <DialogContent dividers>
        {isInitializing ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {STEPS.map((step, index) => (
                <Step key={step.key} completed={index < activeStep}>
                  <StepLabel
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleStepClick(index)}
                  >
                    {step.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box sx={{ minHeight: 400 }}>
              {renderStepContent()}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={requestClose} disabled={isSaving}>
          Abbrechen
        </Button>
        <Box sx={{ flex: 1 }} />
        {isEditMode && activeStep < STEPS.length - 1 && (
          <Button
            variant="outlined"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : null}
          >
            {isSaving ? 'Speichern...' : 'Aktualisieren'}
          </Button>
        )}
        <Button
          startIcon={<BackIcon />}
          onClick={handleBack}
          disabled={activeStep === 0 || isSaving}
        >
          Zurück
        </Button>
        {activeStep < STEPS.length - 1 ? (
          <Button
            variant="contained"
            endIcon={<NextIcon />}
            onClick={handleNext}
            disabled={!canProceedToNextStep() || isSaving}
          >
            Weiter
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || !canProceedToNextStep()}
            startIcon={isSaving ? <CircularProgress size={16} /> : null}
          >
            {isSaving ? 'Speichern...' : (createdProductId ? 'Aktualisieren' : 'Erstellen')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
    </>
  );
}
